import React, { useEffect, useMemo, useState } from 'react';
import { BarChart3, BookOpen, CheckCircle, ExternalLink, LogOut, Plus, RefreshCw, Search, Settings, ShieldCheck, Trash2, X } from 'lucide-react';
import {
  applyFeedbackCorrectionAPI,
  checkBackendHealthAPI,
  deleteVerifiedProductAPI,
  getAnalyticsSummaryAPI,
  getCostControlsAPI,
  getReviewQueueAPI,
  listOpsEventsAPI,
  listFeedbackInboxAPI,
  listVerifiedCatalogAPI,
  restoreVerifiedProductAPI,
  saveVerifiedProductAPI,
  sendBackendSentryTestAPI,
  updateFeedbackStatusAPI,
  updateOpsEventStatusAPI,
  updateReviewQueueStatusAPI,
  updateReviewQueueCategoryAPI,
} from '../utils/api';
import { captureFrontendMessage } from '../sentry';
import { LearningArchivePanel } from './LearningArchivePanel';

const blankForm = {
  name: '',
  brand: '',
  aliases: '',
  ingredients: '',
  status: 'vegan',
  sourceUrl: '',
  sourceType: 'manufacturer',
  verificationReason: '',
  verifiedBy: '',
  notes: '',
  expiresAt: '',
};

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'needs_review', label: 'Needs review' },
  { id: 'user_flag', label: 'User flagged' },
  { id: 'source_conflict', label: 'Source conflicts' },
];

const FLAG_CATEGORIES = [
  { id: 'vegan_status', label: 'Vegan status' },
  { id: 'product_match', label: 'Product match' },
  { id: 'ingredients', label: 'Ingredients' },
  { id: 'flavor_variant', label: 'Flavor or variant' },
  { id: 'allergen', label: 'Allergen' },
  { id: 'alternatives', label: 'Alternatives' },
  { id: 'source_trust', label: 'Source or trust' },
  { id: 'technical_problem', label: 'Technical problem' },
  { id: 'other', label: 'Other' },
];

const flagCategoryLabel = (value) =>
  FLAG_CATEGORIES.find((category) => category.id === value)?.label || 'Not categorized';

const FAILURE_REASONS = new Set([
  'exact_source_missing',
  'missing_ingredients',
  'barcode_missing_ingredients',
  'barcode_not_found',
  'backend_error',
  'lookup_failed',
]);

const FAILURE_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'source', label: 'Source' },
  { id: 'barcode', label: 'Barcode' },
  { id: 'backend', label: 'Backend' },
];

const FEEDBACK_FILTERS = [
  { id: 'open', label: 'Open' },
  { id: 'reviewing', label: 'Reviewing' },
  { id: 'resolved', label: 'Resolved' },
  { id: 'dismissed', label: 'Dismissed' },
  { id: 'all', label: 'All' },
];

const QUEUE_STATUS_FILTERS = [
  { id: 'open', label: 'Open' },
  { id: 'reviewing', label: 'Investigating' },
  { id: 'resolved', label: 'Resolved' },
  { id: 'dismissed', label: 'Cleared' },
  { id: 'all', label: 'All records' },
];

const VERIFIED_REVIEW_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'needs_review', label: 'Needs re-review' },
  { id: 'expiring_soon', label: 'Expiring soon' },
  { id: 'current', label: 'Current' },
];

const DOC_LINKS = [
  {
    group: 'Start Here',
    links: [
      { label: 'Vision Board', detail: 'Personal project dashboard', href: '/project-dashboard.html' },
      { label: 'Foundation Overview', detail: 'Repo, workflow, and project source of truth', href: '/docs/foundation-overview.html' },
      { label: 'Feature Map', detail: 'Audit index and feature fat files', href: '/docs/feature-map.html' },
      { label: 'Launch Readiness', detail: 'Hybrid public launch plan', href: '/docs/launch-readiness.html' },
      { label: 'Hybrid Launch Plan', detail: 'Web beta first, native iOS scanner next', href: '/docs/hybrid-launch-plan.html' },
      { label: 'Public Web Beta', detail: 'Hosting, auth, and persistence plan', href: '/docs/public-web-beta-plan.html' },
    ],
  },
  {
    group: 'Operations',
    links: [
      { label: 'Admin Review', detail: 'Command, verified products, errors, queue', href: '/docs/features/admin-review.md' },
      { label: 'How To: Analytics', detail: 'Read metrics and recent activity', href: '/docs/how-to-analytics.html' },
      { label: 'Product Lookup', detail: 'Search flow, source matching, variants', href: '/docs/features/product-lookup.md' },
      { label: 'Scanner', detail: 'Barcode/image scan audit and native iOS notes', href: '/docs/features/scanner.md' },
      { label: 'Deployment', detail: 'Mac mini, launchd, Tailscale', href: '/docs/features/deployment.md' },
      { label: 'Security', detail: 'Keys, secrets, and safe handling', href: '/docs/features/security.md' },
    ],
  },
  {
    group: 'Policies',
    links: [
      { label: 'Privacy and Data', detail: 'User-facing privacy foundation', href: '/docs/privacy-user-data.html' },
      { label: 'Retention Archive', detail: 'Short-term evidence, long-term learning', href: '/docs/retention-learning-archive.html' },
      { label: 'Costs and Services', detail: 'Spend and API watchlist', href: '/docs/costs-and-services.html' },
      { label: 'To Do', detail: 'Synced planning tasks', href: '/docs/todo.html' },
    ],
  },
];

function splitLines(value) {
  return String(value || '')
    .split(/\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function formatDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  }).format(date);
}

function formatDateTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function formatRefreshTime() {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date());
}

function formatEventTime(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function verifiedReviewBadge(row) {
  if (row?.reviewStatus === 'needs_review' || row?.recordExpired) {
    return { label: 'Needs re-review', className: 'bg-amber-100 text-amber-950' };
  }
  if (row?.reviewStatus === 'expiring_soon') {
    return { label: 'Expiring soon', className: 'bg-yellow-100 text-yellow-950' };
  }
  return { label: 'Current', className: 'bg-emerald-100 text-emerald-950' };
}

function waitForRefreshCue(startedAt) {
  const remaining = 500 - (Date.now() - startedAt);
  if (remaining <= 0) return Promise.resolve();
  return new Promise((resolve) => window.setTimeout(resolve, remaining));
}

/** Accepts explicit N/A (spacing/case-insensitive) or a short substantive reason (letters, not only symbols/digits). */
function isValidVerificationReason(raw) {
  const s = String(raw || '').trim();
  if (!s) return false;
  const compact = s.replace(/\s+/g, '').toLowerCase();
  if (compact === 'n/a') return true;
  if (s.length < 3) return false;
  if (!/[a-zA-Z]/.test(s)) return false;
  return true;
}

function buildNotesPayload(verificationReason, notes) {
  const reasonLine = `Verification reason: ${String(verificationReason).trim()}`;
  const rest = String(notes || '').trim();
  return rest ? `${reasonLine}\n\n${rest}` : reasonLine;
}

function parseNotesPayload(notes) {
  const raw = String(notes || '').trim();
  const match = raw.match(/^Verification reason:\s*(.*?)(?:\n\n([\s\S]*))?$/i);
  if (!match) {
    return { verificationReason: raw ? 'N/A' : '', notes: raw };
  }
  return {
    verificationReason: String(match[1] || '').trim() || 'N/A',
    notes: String(match[2] || '').trim(),
  };
}

function prefillFromItem(item) {
  const result = item?.lastResult || {};
  const sources = Array.isArray(result.sources) ? result.sources : [];
  const firstSource = sources[0] || {};
  const ingredients = Array.isArray(result.ingredients) ? result.ingredients : [];
  const productName = adminDisplayText(item?.productMatch || result.productMatch || item?.query);

  return {
    ...blankForm,
    name: productName,
    aliases: item?.query && item.query !== productName ? item.query : '',
    ingredients: ingredients.join('\n'),
    status: result.status === 'not-vegan' ? 'not-vegan' : 'vegan',
    sourceUrl: firstSource.url || '',
    sourceType: firstSource.type || 'manufacturer',
    notes: `Reviewed from queue item #${item?.id || ''}. Previous result: ${result.status || 'unknown'}.`,
  };
}

function conciseJson(value) {
  if (!value) return '';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function adminDisplayText(value, fallback = '') {
  if (value == null) return fallback;
  if (typeof value === 'string' || typeof value === 'number') {
    const clean = String(value).trim();
    return clean && clean.toLowerCase() !== '[object object]' ? clean : fallback;
  }
  if (Array.isArray(value)) return value.map((item) => adminDisplayText(item)).filter(Boolean).join(' ') || fallback;
  if (typeof value === 'object') {
    return adminDisplayText(
      value.productMatch || value.productName || value.name || value.label || value.title || value.query || value.input || value.barcode,
      fallback,
    );
  }
  return String(value).trim() || fallback;
}

function reviewItemTitle(item, fallback = 'Review item') {
  return (
    adminDisplayText(item?.productMatch) ||
    adminDisplayText(item?.lastResult?.productMatch) ||
    adminDisplayText(item?.query) ||
    fallback
  );
}

function userFlagSummary(item) {
  const flag = item?.lastResult?.userFlag;
  if (!flag || typeof flag !== 'object') return '';
  return [adminDisplayText(flag.label), adminDisplayText(flag.detail)].filter(Boolean).join(': ');
}

function failureSummary(item) {
  const result = item?.lastResult || {};
  if (!result.failure && !result.error && !FAILURE_REASONS.has(item?.reason)) return null;
  const evidence = result.sourceEvidence || {};
  return {
    error: result.error || item.reason || 'LOOKUP_FAILED',
    message: result.message || 'No message saved.',
    resolvedVia: result.resolvedVia || 'unknown',
    status: result.status || 'unknown',
    productMatch: adminDisplayText(item?.productMatch || result.productMatch),
    query: adminDisplayText(item?.query),
    reason: item?.reason || '',
    createdAt: item?.createdAt || '',
    updatedAt: item?.updatedAt || '',
    sources: Array.isArray(result.sources) ? result.sources : [],
    sourceTypes: Array.isArray(evidence.sourceTypes) ? evidence.sourceTypes : [],
    trustedSourceCount: evidence.trustedSourceCount,
    bestSourceType: evidence.bestSourceType,
    bestTrustScore: evidence.bestTrustScore,
    rejectedSources: Array.isArray(evidence.rejectedSources) ? evidence.rejectedSources : [],
    warnings: Array.isArray(evidence.identityWarnings) ? evidence.identityWarnings : [],
    rawResult: result,
  };
}

function plainEnglishFailure(summary) {
  if (!summary) return 'VegAI saved this because something went wrong, but there is not enough detail yet to explain the failure.';
  const subject = summary.productMatch || summary.query || 'this product';
  if (summary.reason === 'exact_source_missing') {
    return `VegAI found possible sources for ${subject}, but it did not trust any of them enough to analyze. This usually means the product name, flavor, size, or source evidence did not match cleanly.`;
  }
  if (summary.reason === 'missing_ingredients' || summary.reason === 'barcode_missing_ingredients') {
    return `VegAI found ${subject}, but the source did not include ingredients. Without ingredients, VegAI should not guess a vegan result.`;
  }
  if (summary.reason === 'barcode_not_found') {
    return `VegAI could read or receive the barcode, but the barcode was not found in the trusted product sources. Manual entry may work, but the product needs better source coverage.`;
  }
  if (summary.reason === 'backend_error' || summary.error) {
    return `The backend reported an error while processing ${subject}. Check the raw backend result and logs if this keeps happening.`;
  }
  if (summary.reason === 'lookup_failed') {
    return `VegAI could not complete the lookup for ${subject}. The likely issue is product-source coverage, unclear naming, or a failed resolver step.`;
  }
  return `VegAI could not confidently complete this request for ${subject}. Review the source evidence and rejected sources to decide whether this needs a verified record or a code/source fix.`;
}

function isFailureItem(item) {
  return Boolean(item?.lastResult?.failure || item?.lastResult?.error || FAILURE_REASONS.has(item?.reason));
}

function searchableText(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(searchableText).join(' ');
  if (typeof value === 'object') return Object.values(value).map(searchableText).join(' ');
  return String(value);
}

function matchesSearch(value, query) {
  const needle = String(query || '').trim().toLowerCase();
  if (!needle) return true;
  return searchableText(value).toLowerCase().includes(needle);
}

function matchesQueueFilter(item, filter) {
  if (filter === 'all') return true;
  if (filter === 'needs_review') return ['single_uncertain', 'repeated_uncertainty', 'not_vegan_result'].includes(item.reason);
  if (filter.startsWith('category:')) return item.flagCategory === filter.slice('category:'.length);
  return item.reason === filter;
}

function matchesFailureFilter(item, filter) {
  if (filter === 'all') return true;
  if (filter === 'source') return ['exact_source_missing', 'missing_ingredients'].includes(item.reason);
  if (filter === 'barcode') return ['barcode_missing_ingredients', 'barcode_not_found'].includes(item.reason);
  if (filter === 'backend') return ['backend_error', 'lookup_failed'].includes(item.reason) || Boolean(item?.lastResult?.error);
  return item.reason === filter;
}

function SearchBox({ value, onChange, placeholder, helper }) {
  return (
    <label className="block space-y-1">
      <span className="relative block">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
        <input
          type="search"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          className="w-full rounded-xl border border-slate-800 bg-slate-950/70 py-2.5 pl-10 pr-3 text-sm text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-emerald-400/60"
        />
      </span>
      {helper ? <span className="block px-1 text-xs text-slate-500">{helper}</span> : null}
    </label>
  );
}

function WorkCard({ title, actionLabel, onAction, children }) {
  return (
    <section className="rounded-3xl border border-slate-800/80 bg-slate-950/60 p-5 shadow-2xl shadow-black/10 backdrop-blur">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-white">{title}</h2>
        {onAction ? (
          <button type="button" onClick={onAction} className="text-sm font-semibold text-emerald-300 hover:text-emerald-200">
            {actionLabel}
          </button>
        ) : null}
      </div>
      <div className="mt-4 space-y-3">{children}</div>
    </section>
  );
}

function CommandFeedback({ error, message, lastDeletedVerified, onUndoDelete, loading }) {
  if (!error && !message) return null;
  return (
    <div className="space-y-3" aria-live="polite">
      {error ? (
        <p role="alert" className="rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200">
          {error}
        </p>
      ) : null}
      {message ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-400/20 bg-emerald-400/10 p-3 text-sm text-emerald-200">
          <p>{message}</p>
          {lastDeletedVerified ? (
            <button
              type="button"
              onClick={onUndoDelete}
              disabled={loading}
              className="rounded-lg border border-emerald-300/40 px-3 py-1.5 font-semibold text-emerald-100 transition hover:bg-emerald-300/10 disabled:opacity-50"
            >
              Undo Delete
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function MiniWorkItem({ title, meta, body, onClick }) {
  return (
    <button type="button" onClick={onClick} className="w-full rounded-2xl border border-slate-800 bg-slate-950/50 p-4 text-left transition hover:border-slate-700">
      <p className="font-semibold text-white">{title || 'Untitled'}</p>
      {body ? <p className="mt-1 line-clamp-2 text-sm text-slate-400">{body}</p> : null}
      {meta ? <p className="mt-2 text-xs text-slate-500">{meta}</p> : null}
    </button>
  );
}

function HealthRow({ label, value, detail }) {
  const ok = value === 'ok';
  return (
    <div className="flex items-start justify-between gap-3 rounded-2xl border border-slate-800 bg-slate-950/50 p-4">
      <div>
        <p className="font-semibold text-white">{label}</p>
        {detail ? <p className="mt-1 text-xs text-slate-500">{detail}</p> : null}
      </div>
      <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${ok ? 'bg-emerald-400/15 text-emerald-200' : 'bg-amber-400/15 text-amber-200'}`}>
        {value || 'unknown'}
      </span>
    </div>
  );
}

function AnalyticsMetric({ label, value, detail, tone = 'slate' }) {
  const toneClass = tone === 'emerald'
    ? 'border-emerald-400/25 bg-emerald-400/10'
    : tone === 'amber'
      ? 'border-amber-400/25 bg-amber-400/10'
      : tone === 'sky'
        ? 'border-sky-400/25 bg-sky-400/10'
        : 'border-slate-800 bg-slate-950/50';
  return (
    <div className={`rounded-2xl border p-4 ${toneClass}`}>
      <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">{label}</p>
      <p className="mt-2 text-2xl font-bold text-white">{value ?? 0}</p>
      {detail ? <p className="mt-1 text-xs text-slate-500">{detail}</p> : null}
    </div>
  );
}

function analyticsLabel(value) {
  const labels = {
    app: 'App visit',
    manual: 'Manual analysis',
    product: 'Product lookup',
    scanner: 'Scanner',
    barcode: 'Barcode',
    image: 'Image scan',
    feedback: 'Feedback',
    favorites: 'Favorites',
    history: 'History',
    navigation: 'Navigation',
    page_view: 'Page view',
    tab_view: 'Tab view',
    analysis_submit: 'Analysis submitted',
    analysis_success: 'Analysis succeeded',
    analysis_failure: 'Analysis failed',
    feedback_open: 'Feedback opened',
    feedback_submit: 'Feedback submitted',
    favorite_save: 'Favorite saved',
    favorite_toggle: 'Favorite toggled',
    history_rerun: 'History rerun',
  };
  return labels[value] || value || 'unknown';
}

function timeValue(value) {
  const time = new Date(value || '').getTime();
  return Number.isFinite(time) ? time : 0;
}

function sameLookup(a, b) {
  const left = String(a?.productMatch || a?.query || '').toLowerCase();
  const right = String(b?.productMatch || b?.query || '').toLowerCase();
  return left && right && (left === right || left.includes(right) || right.includes(left));
}

function findNearbyCostEvent(lookup, events = []) {
  const lookupTime = timeValue(lookup?.createdAt);
  if (!lookupTime) return null;
  return (events || [])
    .map((event) => ({ event, distance: Math.abs(timeValue(event.createdAt) - lookupTime) }))
    .filter(({ distance }) => distance <= 5 * 60 * 1000)
    .sort((a, b) => a.distance - b.distance)[0]?.event || null;
}

function analyticsStatusTone(status) {
  if (status === 'vegan') return 'bg-emerald-400/15 text-emerald-200';
  if (status === 'not-vegan') return 'bg-rose-400/15 text-rose-200';
  if (status === 'maybe-vegan' || status === 'unknown') return 'bg-amber-400/15 text-amber-200';
  return 'bg-slate-800 text-slate-200';
}

function DocLinkCard({ label, detail, href }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="group flex items-start justify-between gap-3 rounded-2xl border border-slate-800 bg-slate-950/50 p-4 text-left transition hover:border-emerald-400/40 hover:bg-emerald-400/10"
    >
      <span>
        <span className="block font-semibold text-white">{label}</span>
        <span className="mt-1 block text-sm leading-5 text-slate-500">{detail}</span>
      </span>
      <ExternalLink className="mt-1 h-4 w-4 shrink-0 text-slate-600 transition group-hover:text-emerald-300" />
    </a>
  );
}

function prefillFromVerifiedProduct(row) {
  const sources = Array.isArray(row?.sources) ? row.sources : [];
  const firstSource = sources[0] || {};
  const parsedNotes = parseNotesPayload(row?.notes);

  return {
    ...blankForm,
    name: row?.name || '',
    brand: row?.brand || '',
    aliases: Array.isArray(row?.aliases) ? row.aliases.join('\n') : '',
    ingredients: Array.isArray(row?.ingredients) ? row.ingredients.join('\n') : '',
    status: row?.status || 'vegan',
    sourceUrl: firstSource.url || '',
    sourceType: firstSource.type || firstSource.label || 'manufacturer',
    verificationReason: parsedNotes.verificationReason,
    verifiedBy: row?.verifiedBy || '',
    notes: parsedNotes.notes,
    expiresAt: row?.expiresAt ? String(row.expiresAt).slice(0, 10) : '',
  };
}

export function AdminPanel() {
  const [secret, setSecret] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [adminView, setAdminView] = useState('overview');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [verifiedRows, setVerifiedRows] = useState([]);
  const [feedbackItems, setFeedbackItems] = useState([]);
  const [opsEvents, setOpsEvents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [editingVerified, setEditingVerified] = useState(null);
  const [confirmingVerifiedDeleteId, setConfirmingVerifiedDeleteId] = useState(null);
  const [lastDeletedVerified, setLastDeletedVerified] = useState(null);
  const [manualMode, setManualMode] = useState(false);
  const [queueFilter, setQueueFilter] = useState('all');
  const [failureFilter, setFailureFilter] = useState('all');
  const [queueStatusFilter, setQueueStatusFilter] = useState('open');
  const [feedbackFilter, setFeedbackFilter] = useState('open');
  const [verifiedReviewFilter, setVerifiedReviewFilter] = useState('all');
  const [workspaceSearch, setWorkspaceSearch] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewFlagCategory, setReviewFlagCategory] = useState('');
  const [feedbackNotes, setFeedbackNotes] = useState('');
  const [serviceHealth, setServiceHealth] = useState(null);
  const [costActivity, setCostActivity] = useState(null);
  const [analyticsSummary, setAnalyticsSummary] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const canLoad = secret.trim().length > 0;

  useEffect(() => {
    let targetId = '';
    if (editingVerified) targetId = 'command-verified-editor';
    else if (selectedFeedback) targetId = 'command-feedback-detail';
    else if (selected || manualMode) targetId = 'command-queue-detail';
    if (!targetId) return;
    window.requestAnimationFrame(() => {
      const target = document.getElementById(targetId);
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      target?.querySelector('input, select, textarea, button')?.focus({ preventScroll: true });
    });
  }, [adminView, editingVerified, manualMode, selected, selectedFeedback]);

  const selectedSources = useMemo(() => {
    if (editingVerified) {
      return Array.isArray(editingVerified.sources) ? editingVerified.sources : [];
    }
    const result = selected?.lastResult || {};
    return Array.isArray(result.sources) ? result.sources : [];
  }, [editingVerified, selected]);

  const filteredItems = useMemo(() => {
    const reviewItems = items.filter((item) => !isFailureItem(item));
    return reviewItems.filter((item) => matchesQueueFilter(item, queueFilter) && matchesSearch(item, workspaceSearch));
  }, [items, queueFilter, workspaceSearch]);

  const failureItems = useMemo(() => {
    const failures = items.filter((item) => isFailureItem(item));
    return failures.filter((item) => matchesFailureFilter(item, failureFilter) && matchesSearch(item, workspaceSearch));
  }, [items, failureFilter, workspaceSearch]);

  const reviewItems = useMemo(() => items.filter((item) => !isFailureItem(item)), [items]);
  const allFailureItems = useMemo(() => items.filter((item) => isFailureItem(item)), [items]);
  const searchedOpsEvents = useMemo(
    () => opsEvents.filter((item) => matchesSearch(item, workspaceSearch)),
    [opsEvents, workspaceSearch]
  );
  const searchedFeedbackItems = useMemo(
    () => feedbackItems.filter((item) => matchesSearch(item, workspaceSearch)),
    [feedbackItems, workspaceSearch]
  );
  const searchedVerifiedRows = useMemo(
    () => verifiedRows.filter((row) => {
      const matchesFilter =
        verifiedReviewFilter === 'all' ||
        (verifiedReviewFilter === 'needs_review' && (row.needsReview || row.recordExpired)) ||
        row.reviewStatus === verifiedReviewFilter;
      return matchesFilter && matchesSearch(row, workspaceSearch);
    }),
    [verifiedRows, verifiedReviewFilter, workspaceSearch]
  );
  const verifiedCatalogCounts = useMemo(() => ({
    total: verifiedRows.length,
    needsReview: verifiedRows.filter((row) => row.needsReview || row.recordExpired).length,
    expiringSoon: verifiedRows.filter((row) => row.reviewStatus === 'expiring_soon').length,
    current: verifiedRows.filter((row) => row.reviewStatus === 'current').length,
  }), [verifiedRows]);
  async function loadWorkspace(status = queueStatusFilter) {
    setError('');
    setMessage('Refreshing Command...');
    setLoading(true);
    const refreshStartedAt = Date.now();
    try {
      const [queue, feedback, catalog, costs, ops, analytics] = await Promise.all([
        getReviewQueueAPI({ secret, status, limit: 200 }),
        listFeedbackInboxAPI({ secret, status: feedbackFilter, limit: 200 }),
        listVerifiedCatalogAPI({ secret, limit: 300 }),
        getCostControlsAPI({ secret, limit: 12 }),
        listOpsEventsAPI({ secret, status: 'open', limit: 100 }),
        getAnalyticsSummaryAPI({ secret, limit: 8 }),
      ]);
      setItems(queue);
      setFeedbackItems(feedback);
      setVerifiedRows(catalog);
      setCostActivity(costs);
      setOpsEvents(ops);
      setAnalyticsSummary(analytics);
      setAuthenticated(true);
      setMessage(`Command refreshed at ${formatRefreshTime()}.`);
      if (queue.length === 0) setSelected(null);
      if (feedback.length === 0) {
        setSelectedFeedback(null);
        setFeedbackNotes('');
      }
    } catch (err) {
      setError(err.message || 'Could not load admin workspace.');
    } finally {
      await waitForRefreshCue(refreshStartedAt);
      setLoading(false);
    }
  }

  async function loadQueue(status = queueStatusFilter) {
    setError('');
    setMessage('Refreshing queue...');
    setLoading(true);
    const refreshStartedAt = Date.now();
    try {
      const queue = await getReviewQueueAPI({ secret, status, limit: 200 });
      setItems(queue);
      setAuthenticated(true);
      setMessage(`Queue refreshed at ${formatRefreshTime()}.`);
      if (queue.length === 0) setSelected(null);
    } catch (err) {
      setError(err.message || 'Could not load review queue.');
    } finally {
      await waitForRefreshCue(refreshStartedAt);
      setLoading(false);
    }
  }

  async function loadOpsEvents(status = 'open') {
    setError('');
    setMessage('Refreshing errors...');
    setLoading(true);
    const refreshStartedAt = Date.now();
    try {
      const rows = await listOpsEventsAPI({ secret, status, limit: 100 });
      setOpsEvents(rows);
      setMessage(`Errors refreshed at ${formatRefreshTime()}.`);
    } catch (err) {
      setError(err.message || 'Could not load operations events.');
    } finally {
      await waitForRefreshCue(refreshStartedAt);
      setLoading(false);
    }
  }

  async function loadVerifiedCatalog() {
    setError('');
    setMessage('Refreshing catalog...');
    setLoading(true);
    const refreshStartedAt = Date.now();
    try {
      const rows = await listVerifiedCatalogAPI({ secret, limit: 300 });
      setVerifiedRows(rows);
      setMessage(`Catalog refreshed at ${formatRefreshTime()}.`);
    } catch (err) {
      setError(err.message || 'Could not load verified catalog.');
    } finally {
      await waitForRefreshCue(refreshStartedAt);
      setLoading(false);
    }
  }

  async function loadFeedbackInbox(status = feedbackFilter) {
    setError('');
    setMessage('Refreshing feedback...');
    setLoading(true);
    const refreshStartedAt = Date.now();
    try {
      const rows = await listFeedbackInboxAPI({ secret, status, limit: 200 });
      setFeedbackItems(rows);
      setMessage(`Feedback refreshed at ${formatRefreshTime()}.`);
      if (rows.length === 0) {
        setSelectedFeedback(null);
        setFeedbackNotes('');
      }
    } catch (err) {
      setError(err.message || 'Could not load feedback inbox.');
    } finally {
      await waitForRefreshCue(refreshStartedAt);
      setLoading(false);
    }
  }

  async function loadAnalytics() {
    setError('');
    setMessage('Refreshing analytics...');
    setLoading(true);
    const refreshStartedAt = Date.now();
    try {
      const analytics = await getAnalyticsSummaryAPI({ secret, limit: 12 });
      setAnalyticsSummary(analytics);
      setMessage(`Analytics refreshed at ${formatRefreshTime()}.`);
    } catch (err) {
      setError(err.message || 'Could not load analytics.');
    } finally {
      await waitForRefreshCue(refreshStartedAt);
      setLoading(false);
    }
  }

  async function runServiceChecks() {
    setError('');
    setMessage('Running service checks...');
    setLoading(true);
    const refreshStartedAt = Date.now();
    try {
      const [health, costs] = await Promise.all([
        checkBackendHealthAPI({ deep: true }),
        getCostControlsAPI({ secret, limit: 12 }),
      ]);
      setServiceHealth(health);
      setCostActivity(costs);
      setMessage(`Service checks refreshed at ${formatRefreshTime()}.`);
    } catch (err) {
      setServiceHealth({ status: 'error', message: err.message });
      setError(err.message || 'Could not run service checks.');
    } finally {
      await waitForRefreshCue(refreshStartedAt);
      setLoading(false);
    }
  }

  async function sendSentryTestEvents() {
    setError('');
    setMessage('Sending Sentry test events...');
    setLoading(true);
    try {
      const frontendEventId = captureFrontendMessage('VegAI frontend Sentry test event', {
        source: 'admin_command',
        requestedAt: new Date().toISOString(),
      });
      const backend = await sendBackendSentryTestAPI({ secret });
      const parts = [
        frontendEventId ? 'frontend sent' : 'frontend not configured',
        backend?.sent ? 'backend sent' : 'backend not configured',
      ];
      setMessage(`Sentry test complete: ${parts.join('; ')}.`);
    } catch (err) {
      setError(err.message || 'Could not send Sentry test events.');
    } finally {
      setLoading(false);
    }
  }

  async function switchAdminView(view) {
    setAdminView(view);
    setWorkspaceSearch('');
    setSelected(null);
    setReviewNotes('');
    setSelectedFeedback(null);
    setFeedbackNotes('');
    setEditingVerified(null);
    setConfirmingVerifiedDeleteId(null);
    setManualMode(false);
    setMessage('');
    setError('');
    if (view === 'overview') {
      await loadWorkspace();
    } else if (view === 'analytics') {
      await loadAnalytics();
    } else if (view === 'queue' || view === 'failures') {
      await loadQueue();
      if (view === 'failures') await loadOpsEvents();
    } else if (view === 'verified') {
      await loadVerifiedCatalog();
    } else if (view === 'learning') {
      setMessage('Learning Archive ready.');
    } else if (view === 'docs') {
      setMessage('Docs hub ready.');
    } else {
      await loadFeedbackInbox();
    }
  }

  function selectItem(item) {
    setSelected(item);
    setSelectedFeedback(null);
    setReviewNotes(item?.adminNotes || '');
    setReviewFlagCategory(item?.flagCategory || '');
    setFeedbackNotes('');
    setEditingVerified(null);
    setManualMode(false);
    setForm(prefillFromItem(item));
    setMessage('');
    setError('');
  }

  function selectVerifiedRow(row) {
    setSelected(null);
    setReviewNotes('');
    setSelectedFeedback(null);
    setFeedbackNotes('');
    setEditingVerified(row);
    setConfirmingVerifiedDeleteId(null);
    setManualMode(false);
    setForm(prefillFromVerifiedProduct(row));
    setMessage('');
    setError('');
  }

  function selectFeedback(item) {
    setSelected(null);
    setReviewNotes('');
    setEditingVerified(null);
    setManualMode(false);
    setSelectedFeedback(item);
    setFeedbackNotes(item?.adminNotes || '');
    setMessage('');
    setError('');
  }

  function startManualProduct() {
    setAdminView('queue');
    setSelected(null);
    setReviewNotes('');
    setSelectedFeedback(null);
    setFeedbackNotes('');
    setEditingVerified(null);
    setManualMode(true);
    setForm(blankForm);
    setMessage('');
    setError('');
  }

  /** Exit the editor without saving (manual entry or queue detail). */
  function closeEditor() {
    setSelected(null);
    setReviewNotes('');
    setSelectedFeedback(null);
    setFeedbackNotes('');
    setEditingVerified(null);
    setConfirmingVerifiedDeleteId(null);
    setManualMode(false);
    setForm(blankForm);
    setMessage('');
    setError('');
  }

  async function changeFeedbackStatus(status) {
    if (!selectedFeedback) return;
    setError('');
    setMessage('');
    try {
      await updateFeedbackStatusAPI({
        secret,
        id: selectedFeedback.id,
        status,
        adminNotes: feedbackNotes,
      });
      setMessage(`Feedback marked ${status}.`);
      await loadFeedbackInbox(feedbackFilter);
      setSelectedFeedback(null);
      setFeedbackNotes('');
    } catch (err) {
      setError(err.message || 'Could not update feedback item.');
    }
  }

  async function applySelectedFeedbackCorrection() {
    if (!selectedFeedback) return;
    setError('');
    setMessage('');
    try {
      const updated = await applyFeedbackCorrectionAPI({
        secret,
        id: selectedFeedback.id,
        adminNotes: feedbackNotes,
      });
      setSelectedFeedback(updated);
      setFeedbackNotes(updated?.adminNotes || '');
      const result = updated?.correctionResult;
      setMessage(
        result?.applied
          ? `Correction applied for ${result.ingredient}.`
          : `Correction not applied: ${result?.reason || 'unknown'}.`,
      );
      await loadFeedbackInbox(feedbackFilter);
    } catch (err) {
      setError(err.message || 'Could not apply correction.');
    }
  }

  function logout() {
    setSecret('');
    setAuthenticated(false);
    setSettingsOpen(false);
    setItems([]);
    setVerifiedRows([]);
    setFeedbackItems([]);
    setOpsEvents([]);
    setCostActivity(null);
    setAdminView('queue');
    setSelected(null);
    setReviewNotes('');
    setSelectedFeedback(null);
    setFeedbackNotes('');
    setEditingVerified(null);
    setManualMode(false);
    setMessage('');
    setError('');
  }

  async function saveVerified() {
    setError('');
    setMessage('');
    try {
      if (!isValidVerificationReason(form.verificationReason)) {
        setError(
          'Enter a verification reason (why this status), or type N/A if there is none. Blank, spaces-only, or non-text gibberish is not allowed.',
        );
        return;
      }
      const sources = form.sourceUrl.trim()
        ? [{
            type: form.sourceType.trim() || 'manufacturer',
            url: form.sourceUrl.trim(),
            title: form.name.trim() || form.sourceUrl.trim(),
            name: form.name.trim() || undefined,
          }]
        : selectedSources;
      await saveVerifiedProductAPI({
        secret,
        product: {
          id: editingVerified?.id,
          reviewQueueId: selected?.id,
          name: form.name,
          brand: form.brand,
          aliases: splitLines(form.aliases),
          ingredients: splitLines(form.ingredients),
          status: form.status,
          sources,
          verifiedBy: form.verifiedBy || 'admin',
          verificationMethod: 'manual_admin_ui',
          notes: buildNotesPayload(form.verificationReason, form.notes),
          expiresAt: form.expiresAt,
        },
      });
      setMessage(`Saved "${form.name}" as verified ${form.status}.`);
      setSelected(null);
      setEditingVerified(null);
      setConfirmingVerifiedDeleteId(null);
      setManualMode(false);
      if (adminView === 'verified') {
        await loadVerifiedCatalog();
      } else {
        await loadQueue();
      }
    } catch (err) {
      setError(err.message || 'Could not save verified product.');
    }
  }

  async function deleteVerified() {
    if (!editingVerified?.id) return;
    const productName = editingVerified.name || 'this verified product';
    if (confirmingVerifiedDeleteId !== editingVerified.id) {
      setConfirmingVerifiedDeleteId(editingVerified.id);
      setError('');
      setMessage(`Click Confirm Delete to remove "${productName}".`);
      return;
    }
    setError('');
    setMessage(`Deleting "${productName}"...`);
    setLoading(true);
    const refreshStartedAt = Date.now();
    try {
      const deleted = await deleteVerifiedProductAPI({ secret, id: editingVerified.id });
      setVerifiedRows((rows) => rows.filter((row) => row.id !== editingVerified.id));
      setEditingVerified(null);
      setConfirmingVerifiedDeleteId(null);
      setForm(blankForm);
      setLastDeletedVerified(deleted);
      setMessage(`Deleted "${productName}" from the verified catalog.`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      await loadVerifiedCatalog();
    } catch (err) {
      setError(err.message || 'Could not delete verified product.');
    } finally {
      await waitForRefreshCue(refreshStartedAt);
      setLoading(false);
    }
  }

  async function undoVerifiedDelete() {
    if (!lastDeletedVerified) return;
    const productName = lastDeletedVerified.name || 'verified product';
    setError('');
    setMessage(`Restoring "${productName}"...`);
    setLoading(true);
    const refreshStartedAt = Date.now();
    try {
      await restoreVerifiedProductAPI({ secret, deleted: lastDeletedVerified });
      setLastDeletedVerified(null);
      setMessage(`Restored "${productName}" to the verified catalog.`);
      await loadVerifiedCatalog();
    } catch (err) {
      setError(err.message || 'Could not undo verified product deletion.');
    } finally {
      await waitForRefreshCue(refreshStartedAt);
      setLoading(false);
    }
  }

  async function changeReviewItemStatus(item, status, label, { closeAfter = true } = {}) {
    if (!item) return;
    setError('');
    setMessage('');
    try {
      const updated = await updateReviewQueueStatusAPI({ secret, id: item.id, status, adminNotes: reviewNotes });
      setMessage(`${label || 'Updated'} "${reviewItemTitle(item)}".`);
      if (selected?.id === item.id && !closeAfter) {
        setSelected(updated?.id ? updated : { ...item, status, adminNotes: reviewNotes });
      }
      if (selected?.id === item.id && closeAfter) {
        setSelected(null);
        setReviewNotes('');
      }
      await loadQueue(queueStatusFilter);
    } catch (err) {
      setError(err.message || 'Could not update review item.');
    }
  }

  async function saveReviewFlagCategory() {
    if (!selected || !reviewFlagCategory) return;
    setError('');
    setMessage('');
    try {
      const updated = await updateReviewQueueCategoryAPI({
        secret,
        id: selected.id,
        flagCategory: reviewFlagCategory,
      });
      setSelected(updated);
      setItems((current) => current.map((item) => item.id === updated.id ? updated : item));
      setMessage(`Saved category "${flagCategoryLabel(reviewFlagCategory)}".`);
    } catch (err) {
      setError(err.message || 'Could not save flag category.');
    }
  }

  async function dismissSelected() {
    if (!selected) return;
    await changeReviewItemStatus(selected, 'dismissed', 'Cleared');
  }

  async function dismissQueueItem(item, event) {
    event?.stopPropagation();
    if (!item) return;
    await changeReviewItemStatus(item, 'dismissed', 'Cleared');
  }

  async function dismissOpsEvent(item, event) {
    event?.stopPropagation();
    if (!item) return;
    setError('');
    setMessage('');
    try {
      await updateOpsEventStatusAPI({ secret, id: item.id, status: 'dismissed' });
      setMessage(`Cleared "${item.code || item.category}".`);
      await loadOpsEvents();
    } catch (err) {
      setError(err.message || 'Could not clear operations event.');
    }
  }

  async function saveReviewNotes() {
    if (!selected) return;
    await changeReviewItemStatus(selected, selected.status || queueStatusFilter || 'open', 'Saved notes for', { closeAfter: false });
  }

  function handleLogin(event) {
    event.preventDefault();
    if (!canLoad || loading) return;
    loadWorkspace();
  }

  if (!authenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#07111f] p-4 text-slate-100">
        <form
          onSubmit={handleLogin}
          className="w-full max-w-md rounded-3xl border border-slate-800/80 bg-slate-950/80 p-6 shadow-2xl shadow-black/30"
        >
          <div className="flex items-center gap-2 text-emerald-700">
            <ShieldCheck className="h-5 w-5" />
            <span className="text-sm font-bold uppercase tracking-[0.18em] text-emerald-400">VegAI Admin</span>
          </div>
          <h1 className="mt-2 text-3xl font-bold text-white">Admin Login</h1>
          <p className="mt-2 text-sm text-slate-400">
            Enter your admin secret once to load the verified-product review tools.
          </p>
          <label className="mt-5 block space-y-2 text-sm font-semibold text-slate-200">
            Admin Secret
            <input
              type="password"
              value={secret}
              onChange={(event) => setSecret(event.target.value)}
              className="w-full rounded-xl border border-slate-700 bg-slate-900/80 px-3 py-2 text-white outline-none transition placeholder:text-slate-600 focus:border-emerald-400 focus:ring-4 focus:ring-emerald-400/10"
              placeholder="Paste your admin secret"
            />
          </label>
          <button
            type="submit"
            disabled={!canLoad || loading}
            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 px-4 py-2 font-semibold text-slate-950 transition hover:bg-emerald-400 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Log In
          </button>
          {error && <p className="mt-4 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200">{error}</p>}
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#07111f] p-4 text-slate-100 sm:p-6">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,0.12),transparent_34%),radial-gradient(circle_at_top_right,rgba(59,130,246,0.12),transparent_30%)]" />
      <div className="relative mx-auto max-w-7xl space-y-6">
        <div className="rounded-3xl border border-slate-800/80 bg-slate-950/70 p-6 shadow-2xl shadow-black/20 backdrop-blur">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div>
                <div className="flex items-center gap-2 text-emerald-400">
                  <ShieldCheck className="h-5 w-5" />
                  <span className="text-sm font-bold uppercase tracking-[0.18em]">VegAI Admin</span>
                </div>
                <h1 className="mt-3 text-3xl font-bold tracking-tight text-white md:text-4xl">Command</h1>
                <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-400">
                  The daily operations center for reviews, errors, feedback, source health, and verified records.
                </p>
              </div>
              <div className="flex w-fit items-center gap-2 self-start">
                <button
                  type="button"
                  onClick={() => {
                    setSettingsOpen(true);
                    runServiceChecks();
                  }}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-800 bg-slate-950 text-slate-400 transition hover:border-emerald-400/40 hover:bg-emerald-400/10 hover:text-emerald-200"
                  aria-label="Open Command settings"
                >
                  <Settings className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={logout}
                  className="inline-flex w-fit items-center gap-2 rounded-xl border border-slate-800 bg-slate-950 px-3 py-2 text-sm font-semibold text-slate-400 transition hover:border-red-400/30 hover:bg-red-500/10 hover:text-red-200"
                >
                  <LogOut className="h-4 w-4" />
                  Log out
                </button>
              </div>
            </div>

            <div className="grid w-full grid-cols-3 gap-2 border-b border-slate-800 pb-3 sm:inline-flex sm:w-auto sm:flex-wrap sm:items-center sm:gap-4">
              <button
                type="button"
                onClick={() => switchAdminView('overview')}
                className={`rounded-none border-b-2 px-1 py-2 text-sm font-semibold transition ${
                  adminView === 'overview' ? 'border-emerald-400 text-emerald-300' : 'border-transparent text-slate-400 hover:text-slate-200'
                }`}
              >
                Overview
              </button>
              <button
                type="button"
                onClick={() => switchAdminView('analytics')}
                className={`rounded-none border-b-2 px-1 py-2 text-sm font-semibold transition ${
                  adminView === 'analytics' ? 'border-emerald-400 text-emerald-300' : 'border-transparent text-slate-400 hover:text-slate-200'
                }`}
              >
                Analytics
              </button>
              <button
                type="button"
                onClick={() => switchAdminView('queue')}
                className={`rounded-none border-b-2 px-1 py-2 text-sm font-semibold transition ${
                  adminView === 'queue' ? 'border-emerald-400 text-emerald-300' : 'border-transparent text-slate-400 hover:text-slate-200'
                }`}
              >
                Review queue
              </button>
              <button
                type="button"
                onClick={() => switchAdminView('failures')}
                className={`rounded-none border-b-2 px-1 py-2 text-sm font-semibold transition ${
                  adminView === 'failures' ? 'border-emerald-400 text-emerald-300' : 'border-transparent text-slate-400 hover:text-slate-200'
                }`}
              >
                Errors
              </button>
              <button
                type="button"
                onClick={() => switchAdminView('feedback')}
                className={`rounded-none border-b-2 px-1 py-2 text-sm font-semibold transition ${
                  adminView === 'feedback' ? 'border-emerald-400 text-emerald-300' : 'border-transparent text-slate-400 hover:text-slate-200'
                }`}
              >
                Feedback
              </button>
              <button
                type="button"
                onClick={() => switchAdminView('verified')}
                className={`rounded-none border-b-2 px-1 py-2 text-sm font-semibold transition ${
                  adminView === 'verified' ? 'border-emerald-400 text-emerald-300' : 'border-transparent text-slate-400 hover:text-slate-200'
                }`}
              >
                Verified catalog
              </button>
              <button
                type="button"
                onClick={() => switchAdminView('learning')}
                className={`rounded-none border-b-2 px-1 py-2 text-sm font-semibold transition ${
                  adminView === 'learning' ? 'border-emerald-400 text-emerald-300' : 'border-transparent text-slate-400 hover:text-slate-200'
                }`}
              >
                Learning Archive
              </button>
              <button
                type="button"
                onClick={() => switchAdminView('docs')}
                className={`rounded-none border-b-2 px-1 py-2 text-sm font-semibold transition ${
                  adminView === 'docs' ? 'border-emerald-400 text-emerald-300' : 'border-transparent text-slate-400 hover:text-slate-200'
                }`}
              >
                Docs
              </button>
            </div>

            <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-start">
              {adminView === 'overview' ? (
                <p className="rounded-xl border border-slate-800 bg-slate-950/50 px-4 py-3 text-sm text-slate-400">
                  Start here for today’s next action. Open a tab when you need search, filters, or full record details.
                </p>
              ) : (
                <SearchBox
                  value={workspaceSearch}
                  onChange={setWorkspaceSearch}
                  placeholder={`Search ${adminView === 'learning' ? 'lessons, products, or tags' : adminView === 'verified' ? 'verified catalog' : adminView === 'feedback' ? 'feedback' : adminView === 'failures' ? 'errors' : adminView === 'docs' ? 'docs' : 'review queue'}`}
                  helper={
                    adminView === 'docs'
                      ? 'Searches doc titles and descriptions in this tab.'
                      : 'Searches the currently loaded records in this tab. Use status filters to load archived or cleared records.'
                  }
                />
              )}
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-start lg:justify-end">
              {adminView !== 'docs' && adminView !== 'learning' && (
                <button
                  type="button"
                  onClick={startManualProduct}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-4 py-2.5 font-semibold text-white transition hover:border-slate-500 hover:bg-slate-800 sm:w-auto"
                >
                  <Plus className="h-4 w-4" />
                  New Verified Product
                </button>
              )}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (adminView === 'overview') return loadWorkspace();
                    if (adminView === 'analytics') return loadAnalytics();
                    if (adminView === 'verified') return loadVerifiedCatalog();
                    if (adminView === 'feedback') return loadFeedbackInbox();
                    if (adminView === 'learning') return setMessage('Learning Archive reloads when you open the tab.');
                    if (adminView === 'docs') return setMessage('Docs hub ready.');
                    return loadQueue();
                  }}
                  disabled={!canLoad || loading}
                  className="inline-flex min-w-[10rem] flex-1 items-center justify-center gap-2 rounded-xl border border-emerald-400/40 bg-emerald-500/15 px-4 py-2 font-semibold text-emerald-200 transition hover:bg-emerald-500/25 disabled:opacity-50 sm:flex-initial"
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  {adminView === 'overview' ? 'Refresh overview' : adminView === 'analytics' ? 'Refresh analytics' : adminView === 'verified' ? 'Refresh catalog' : adminView === 'feedback' ? 'Refresh feedback' : adminView === 'learning' ? 'Archive is current' : adminView === 'failures' ? 'Refresh errors' : adminView === 'docs' ? 'Refresh docs' : 'Refresh queue'}
                </button>
              </div>
              </div>
            </div>
          </div>
        </div>

        {adminView === 'overview' ? (
          <div className="grid gap-4">
            <section className="rounded-3xl border border-slate-800/80 bg-slate-950/60 p-5 shadow-2xl shadow-black/10 backdrop-blur">
              <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-emerald-300">Today</p>
                  <h2 className="mt-2 text-2xl font-bold text-white">
                    {allFailureItems.length
                      ? 'Start with errors.'
                      : reviewItems.length
                        ? 'Review product decisions.'
                        : feedbackItems.length
                          ? 'Check feedback.'
                          : 'Run service checks.'}
                  </h2>
                  <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-400">
                    {allFailureItems.length
                      ? 'Errors are the clearest signals that something failed for a user or source. Investigate those before queue cleanup.'
                      : reviewItems.length
                        ? 'The review queue contains product decisions that can improve future searches once verified.'
                        : feedbackItems.length
                          ? 'Feedback is waiting for a quick owner review.'
                          : 'No urgent work is loaded. Run checks, then continue launch prep.'}
                  </p>
                </div>
                <div className="grid min-w-[18rem] gap-2 sm:grid-cols-3 lg:grid-cols-1">
                  <button type="button" onClick={() => switchAdminView('failures')} className="rounded-2xl border border-slate-800 bg-slate-950/70 p-3 text-left transition hover:border-amber-300/50">
                    <span className="block text-xs font-bold uppercase tracking-wide text-slate-500">Errors</span>
                    <span className="mt-1 block text-2xl font-bold text-white">{allFailureItems.length}</span>
                  </button>
                  <button type="button" onClick={() => switchAdminView('queue')} className="rounded-2xl border border-slate-800 bg-slate-950/70 p-3 text-left transition hover:border-emerald-300/50">
                    <span className="block text-xs font-bold uppercase tracking-wide text-slate-500">Review</span>
                    <span className="mt-1 block text-2xl font-bold text-white">{reviewItems.length}</span>
                  </button>
                  <button type="button" onClick={() => switchAdminView('feedback')} className="rounded-2xl border border-slate-800 bg-slate-950/70 p-3 text-left transition hover:border-blue-300/50">
                    <span className="block text-xs font-bold uppercase tracking-wide text-slate-500">Feedback</span>
                    <span className="mt-1 block text-2xl font-bold text-white">{feedbackItems.length}</span>
                  </button>
                </div>
              </div>
            </section>
            <div className="grid gap-4 lg:grid-cols-3">
            <WorkCard title="Needs Review" actionLabel="Open queue" onAction={() => switchAdminView('queue')}>
                {reviewItems.slice(0, 2).map((item) => (
                  <MiniWorkItem
                    key={item.id}
                    title={reviewItemTitle(item)}
                    meta={`${item.reason} · Submitted ${formatDateTime(item.createdAt) || 'unknown'}${item.updatedAt ? ` · Updated ${formatDateTime(item.updatedAt)}` : ''}`}
                    onClick={() => { setAdminView('queue'); selectItem(item); }}
                  />
                ))}
                {reviewItems.length === 0 && <p className="rounded-2xl border border-dashed border-slate-800 p-4 text-sm text-slate-500">No review work loaded.</p>}
            </WorkCard>

            <WorkCard title="Errors To Investigate" actionLabel="Open errors" onAction={() => switchAdminView('failures')}>
                {opsEvents.slice(0, 2).map((item) => (
                  <MiniWorkItem
                    key={`ops-${item.id}`}
                    title={item.code || item.category}
                    body={item.message}
                    meta={`${item.severity} · Submitted ${formatDateTime(item.createdAt) || 'unknown'}${item.updatedAt ? ` · Updated ${formatDateTime(item.updatedAt)}` : ''}`}
                    onClick={() => switchAdminView('failures')}
                  />
                ))}
                {allFailureItems.slice(0, 2).map((item) => (
                  <MiniWorkItem
                    key={item.id}
                    title={reviewItemTitle(item)}
                    meta={`${item.reason} · Submitted ${formatDateTime(item.createdAt) || 'unknown'}${item.updatedAt ? ` · Updated ${formatDateTime(item.updatedAt)}` : ''}`}
                    onClick={() => { setAdminView('failures'); selectItem(item); }}
                  />
                ))}
                {allFailureItems.length === 0 && opsEvents.length === 0 && <p className="rounded-2xl border border-dashed border-slate-800 p-4 text-sm text-slate-500">No errors loaded.</p>}
            </WorkCard>

            <WorkCard title="Feedback Needing Attention" actionLabel="Open feedback" onAction={() => switchAdminView('feedback')}>
                {feedbackItems.slice(0, 2).map((item) => (
                  <MiniWorkItem
                    key={item.id}
                    title={item.category}
                    body={item.message}
                    meta={`${item.status} · Submitted ${formatDateTime(item.createdAt) || 'unknown'}${item.updatedAt ? ` · Updated ${formatDateTime(item.updatedAt)}` : ''}`}
                    onClick={() => { setAdminView('feedback'); selectFeedback(item); }}
                  />
                ))}
                {feedbackItems.length === 0 && <p className="rounded-2xl border border-dashed border-slate-800 p-4 text-sm text-slate-500">No feedback loaded.</p>}
            </WorkCard>

            <WorkCard title="Launch Analytics" actionLabel="Refresh" onAction={() => loadWorkspace()}>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-2xl border border-slate-800 bg-slate-950/50 p-4">
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Searches</p>
                  <p className="mt-2 text-2xl font-bold text-white">{analyticsSummary?.productLookups?.total24h ?? 0}</p>
                  <p className="mt-1 text-xs text-slate-500">Last 24 hours</p>
                </div>
                <div className="rounded-2xl border border-slate-800 bg-slate-950/50 p-4">
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Feedback</p>
                  <p className="mt-2 text-2xl font-bold text-white">{analyticsSummary?.feedback?.open ?? feedbackItems.length}</p>
                  <p className="mt-1 text-xs text-slate-500">Open items</p>
                </div>
              </div>
              <div className="space-y-2">
                {(analyticsSummary?.productLookups?.commonProducts || []).slice(0, 4).map((item) => (
                  <div key={`${item.product}-${item.count}`} className="flex items-center justify-between gap-3 rounded-2xl border border-slate-800 bg-slate-950/50 p-3">
                    <p className="min-w-0 truncate text-sm font-semibold text-white">{item.product}</p>
                    <span className="rounded-full bg-emerald-400/15 px-2.5 py-1 text-xs font-bold text-emerald-200">{item.count}</span>
                  </div>
                ))}
                {(!analyticsSummary?.productLookups?.commonProducts || analyticsSummary.productLookups.commonProducts.length === 0) && (
                  <p className="rounded-2xl border border-dashed border-slate-800 p-4 text-sm text-slate-500">
                    No product search analytics yet.
                  </p>
                )}
              </div>
            </WorkCard>
            </div>
          </div>
        ) : adminView === 'analytics' ? (
          <div className="grid gap-4">
            <section className="rounded-3xl border border-slate-800/80 bg-slate-950/60 p-5 shadow-2xl shadow-black/10 backdrop-blur">
              <div className="flex items-start gap-3">
                <div className="rounded-2xl border border-emerald-400/30 bg-emerald-400/10 p-3 text-emerald-200">
                  <BarChart3 className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-emerald-300">Company-grade signal</p>
                  <h2 className="mt-2 text-2xl font-bold text-white">Analytics</h2>
                  <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-400">
                    The operating story: what users tried, what VegAI returned, what needs review, and whether usage/costs look healthy.
                  </p>
                  {analyticsSummary?.generatedAt ? (
                    <p className="mt-2 text-xs text-slate-500">Generated {formatDateTime(analyticsSummary.generatedAt)}</p>
                  ) : null}
                </div>
              </div>
            </section>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <AnalyticsMetric label="Searches" value={analyticsSummary?.productLookups?.total24h ?? 0} detail="Product checks in 24h" tone="emerald" />
              <AnalyticsMetric label="Needs Review" value={(analyticsSummary?.productLookups?.sourceGapItems?.length ?? 0) + (analyticsSummary?.feedback?.open ?? 0) + (analyticsSummary?.operations?.open ?? 0)} detail="Source gaps, feedback, errors" tone="amber" />
              <AnalyticsMetric label="AI Calls" value={analyticsSummary?.costs?.allowed24h ?? 0} detail={`${analyticsSummary?.costs?.blocked24h ?? 0} blocked`} />
              <AnalyticsMetric label="Visits" value={analyticsSummary?.app?.pageViews ?? 0} detail="Traffic signal" tone="sky" />
            </div>

            <WorkCard title="Recent Search Timeline">
              {(analyticsSummary?.productLookups?.recentLookups || []).map((lookup) => {
                const gap = (analyticsSummary?.productLookups?.sourceGapItems || []).find((item) => sameLookup(item, lookup));
                const aiEvent = findNearbyCostEvent(lookup, analyticsSummary?.costs?.recentEvents);
                return (
                  <details key={`${lookup.query}-${lookup.productMatch}-${lookup.createdAt}`} className="rounded-2xl border border-slate-800 bg-slate-950/50 p-4">
                    <summary className="flex cursor-pointer list-none items-start justify-between gap-3">
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold text-white">{lookup.productMatch || lookup.query || 'Unknown product'}</span>
                        <span className="mt-1 block text-xs text-slate-500">
                          Search: {lookup.query || 'unknown'} · {formatDateTime(lookup.createdAt) || 'No timestamp'}
                        </span>
                        <span className="mt-2 block text-xs text-slate-400">
                          Source: {lookup.resolvedVia || 'unknown'} · Confidence: {lookup.confidence ?? 'n/a'} · Trust: {lookup.sourceEvidence?.bestTrustScore ?? 'n/a'}
                        </span>
                      </span>
                      <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${analyticsStatusTone(lookup.status)}`}>
                        {lookup.status || 'unknown'}
                      </span>
                    </summary>
                    <div className="mt-4 grid gap-3 border-t border-slate-800 pt-4 text-xs text-slate-400 md:grid-cols-3">
                      <div>
                        <p className="font-bold uppercase tracking-[0.14em] text-slate-500">Result</p>
                        <p className="mt-1">Matched: {lookup.productMatch || 'none'}</p>
                        <p>Status: {lookup.status || 'unknown'}</p>
                      </div>
                      <div>
                        <p className="font-bold uppercase tracking-[0.14em] text-slate-500">Source Quality</p>
                        <p className="mt-1">Resolved via: {lookup.resolvedVia || 'unknown'}</p>
                        <p>Best trust: {lookup.sourceEvidence?.bestTrustScore ?? 'n/a'}</p>
                      </div>
                      <div>
                        <p className="font-bold uppercase tracking-[0.14em] text-slate-500">Next Action</p>
                        <p className="mt-1">{gap ? `Review queue: ${gap.reason}` : 'No review item recorded.'}</p>
                        <p>{aiEvent ? `AI: ${aiEvent.allowed ? 'allowed' : 'blocked'} (${aiEvent.reason || 'ok'})` : 'No nearby AI cost event.'}</p>
                      </div>
                    </div>
                  </details>
                );
              })}
              {(!analyticsSummary?.productLookups?.recentLookups || analyticsSummary.productLookups.recentLookups.length === 0) && (
                <p className="rounded-2xl border border-dashed border-slate-800 p-4 text-sm text-slate-500">No product searches recorded yet.</p>
              )}
            </WorkCard>

            <div className="grid gap-4 lg:grid-cols-2">
              <WorkCard title="Needs Attention">
                {(analyticsSummary?.productLookups?.sourceGapItems || []).slice(0, 6).map((item) => (
                  <div key={`${item.query}-${item.reason}-${item.updatedAt}`} className="rounded-2xl border border-amber-400/20 bg-amber-400/10 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-white">{item.productMatch || item.query || 'Unknown product'}</p>
                        <p className="mt-1 text-xs text-amber-100/70">{item.query || 'No query'} · {formatDateTime(item.updatedAt) || 'No timestamp'}</p>
                      </div>
                      <span className="rounded-full bg-amber-400/15 px-2.5 py-1 text-xs font-bold text-amber-100">{item.reason || 'unknown'}</span>
                    </div>
                  </div>
                ))}
                {(!analyticsSummary?.productLookups?.sourceGapItems || analyticsSummary.productLookups.sourceGapItems.length === 0) && (
                  <p className="rounded-2xl border border-dashed border-slate-800 p-4 text-sm text-slate-500">No source gaps need attention in the current window.</p>
                )}
              </WorkCard>

              <WorkCard title="Patterns">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Common Products</p>
                    <div className="mt-2 space-y-2">
                      {(analyticsSummary?.productLookups?.commonProducts || []).slice(0, 4).map((item) => (
                        <div key={`${item.product}-${item.count}-${item.lastSeen}`} className="flex items-center justify-between gap-3 rounded-2xl border border-slate-800 bg-slate-950/50 p-3">
                          <p className="min-w-0 truncate text-sm font-semibold text-white">{item.product || 'Unknown product'}</p>
                          <span className="rounded-full bg-emerald-400/15 px-2.5 py-1 text-xs font-bold text-emerald-200">{item.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Feature Use</p>
                    <div className="mt-2 space-y-2">
                      {(analyticsSummary?.app?.byFeature || []).slice(0, 4).map((item) => (
                        <div key={`${item.feature}-${item.count}-${item.lastSeen}`} className="flex items-center justify-between gap-3 rounded-2xl border border-slate-800 bg-slate-950/50 p-3">
                          <p className="min-w-0 truncate text-sm font-semibold text-white">{analyticsLabel(item.feature)}</p>
                          <span className="rounded-full bg-sky-400/15 px-2.5 py-1 text-xs font-bold text-sky-200">{item.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </WorkCard>
            </div>
          </div>
        ) : adminView === 'learning' ? (
          <LearningArchivePanel secret={secret} search={workspaceSearch} />
        ) : adminView === 'docs' ? (
          <div className="grid gap-4 lg:grid-cols-[1fr_1fr]">
            <section className="rounded-3xl border border-slate-800/80 bg-slate-950/60 p-5 shadow-2xl shadow-black/10 backdrop-blur lg:col-span-2">
              <div className="flex items-start gap-3">
                <div className="rounded-2xl border border-emerald-400/30 bg-emerald-400/10 p-3 text-emerald-200">
                  <BookOpen className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Docs / Review Hub</h2>
                  <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-400">
                    Quick links for reviewing the foundation, feature fat files, launch notes, and operating rules while working in Command.
                  </p>
                </div>
              </div>
            </section>
            {DOC_LINKS.map((section) => {
              const visibleLinks = section.links.filter((link) => matchesSearch(link, workspaceSearch));
              if (visibleLinks.length === 0) return null;
              return (
                <WorkCard key={section.group} title={section.group}>
                  {visibleLinks.map((link) => (
                    <DocLinkCard key={link.href} {...link} />
                  ))}
                </WorkCard>
              );
            })}
          </div>
        ) : (adminView === 'queue' || adminView === 'failures') ? (
        <div className="grid gap-4 lg:grid-cols-[360px_1fr]">
          <div className="rounded-3xl border border-slate-800/80 bg-slate-950/60 p-4 shadow-2xl shadow-black/10 backdrop-blur">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h2 className="text-lg font-bold text-white">{adminView === 'failures' ? 'Backend Errors' : 'Open Queue'}</h2>
              <span className="rounded-full bg-slate-800 px-2.5 py-1 text-xs font-semibold text-slate-300">
                {adminView === 'failures' ? `${failureItems.length + searchedOpsEvents.length}/${allFailureItems.length + opsEvents.length}` : `${filteredItems.length}/${reviewItems.length}`}
              </span>
            </div>
            <div className="mb-4 flex flex-wrap gap-2">
              {QUEUE_STATUS_FILTERS.map((filter) => (
                <button
                  key={filter.id}
                  type="button"
                  onClick={async () => {
                    setQueueStatusFilter(filter.id);
                    await loadQueue(filter.id);
                  }}
                  className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                    queueStatusFilter === filter.id
                      ? 'border-sky-400/40 bg-sky-400/15 text-sky-200'
                      : 'border-slate-800 bg-slate-900/70 text-slate-400 hover:border-slate-700 hover:text-slate-200'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
            <div className="mb-4 flex flex-wrap gap-2">
              {(adminView === 'failures' ? FAILURE_FILTERS : FILTERS).map((filter) => (
                <button
                  key={filter.id}
                  type="button"
                  onClick={() => (adminView === 'failures' ? setFailureFilter(filter.id) : setQueueFilter(filter.id))}
                  className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                    (adminView === 'failures' ? failureFilter : queueFilter) === filter.id
                      ? 'border-emerald-400/40 bg-emerald-400/15 text-emerald-200'
                      : 'border-slate-800 bg-slate-900/70 text-slate-400 hover:border-slate-700 hover:text-slate-200'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
            {adminView === 'queue' ? (
              <label className="mb-4 block space-y-1 text-xs font-semibold text-slate-400">
                Flag category
                <select
                  value={queueFilter.startsWith('category:') ? queueFilter : ''}
                  onChange={(event) => setQueueFilter(event.target.value || 'all')}
                  className="w-full rounded-xl border border-slate-800 bg-slate-950/70 px-3 py-2.5 text-sm text-slate-200 outline-none focus:border-emerald-400/60"
                >
                  <option value="">All categories</option>
                  {FLAG_CATEGORIES.map((category) => (
                    <option key={category.id} value={`category:${category.id}`}>{category.label}</option>
                  ))}
                </select>
              </label>
            ) : null}
            {adminView === 'failures' && searchedOpsEvents.length > 0 && (
              <div className="mb-4 space-y-3">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">System events</p>
                {searchedOpsEvents.map((item) => (
                  <div key={`ops-${item.id}`} className="rounded-2xl border border-amber-400/25 bg-amber-400/10 p-4 text-left">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-amber-100">{item.code || item.category}</p>
                        <p className="mt-1 text-sm leading-5 text-amber-50/80">{item.message}</p>
                        <p className="mt-2 text-xs text-amber-100/60">
                          {[item.method, item.route, formatDateTime(item.updatedAt || item.createdAt)].filter(Boolean).join(' · ')}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={(event) => dismissOpsEvent(item, event)}
                        className="shrink-0 rounded-lg border border-amber-200/30 bg-slate-950/40 px-3 py-1.5 text-xs font-semibold text-amber-100 transition hover:bg-slate-900"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {(adminView === 'failures' ? failureItems.length + searchedOpsEvents.length : filteredItems.length) === 0 ? (
              <p className="text-sm text-slate-500">No items match this filter.</p>
            ) : (
              <div className="space-y-3">
                {(adminView === 'failures' ? failureItems : filteredItems).map((item) => (
                  <div
                    key={item.id}
                    className={`w-full rounded-2xl border p-4 text-left transition ${
                      selected?.id === item.id
                        ? 'border-emerald-400 bg-emerald-400/10 shadow-[0_0_0_1px_rgba(52,211,153,0.18)]'
                        : 'border-slate-800 bg-slate-950/40 hover:border-slate-700 hover:bg-slate-900/70'
                    }`}
                  >
                    <button type="button" onClick={() => selectItem(item)} className="block w-full text-left">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-semibold text-white">{reviewItemTitle(item)}</p>
                          {item.reason === 'user_flag' ? (
                            <span className="rounded-full border border-amber-400/30 bg-amber-400/10 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-amber-200">
                              User flag received
                            </span>
                          ) : null}
                        </div>
                        <div className="mt-2 space-y-1 text-xs text-slate-400">
                          <p>Submitted: {formatDateTime(item.createdAt) || 'unknown'}</p>
                          {item.updatedAt ? <p>Updated: {formatDateTime(item.updatedAt)}</p> : null}
                          <p>Reason: {item.reason}</p>
                          {userFlagSummary(item) ? <p>User note: {userFlagSummary(item)}</p> : null}
                          {item.flagCategory ? <p>Category: {flagCategoryLabel(item.flagCategory)}</p> : null}
                          <p>
                            Result: <span className="text-emerald-300">{item.lastResult?.status || 'unknown'}</span>
                          </p>
                          <p>
                            Uncertain: {item.uncertainCount} <span className="mx-1 text-emerald-400">•</span> Flags: {item.flagCount}
                          </p>
                          {item.adminNotes ? <p className="text-sky-300">Notes saved</p> : null}
                        </div>
                        </div>
                        <span className="mt-1 text-slate-500">›</span>
                      </div>
                    </button>
                    <div className="mt-3 flex justify-end">
                      <button
                        type="button"
                        onClick={(event) => dismissQueueItem(item, event)}
                        className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 text-xs font-semibold text-slate-300 transition hover:bg-slate-800"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div id="command-queue-detail" className="admin-dark-surface scroll-mt-6 rounded-3xl border border-slate-800/80 bg-slate-950/60 p-4 shadow-2xl shadow-black/10 backdrop-blur sm:p-6">
            {!selected && !manualMode ? (
              <div className="flex min-h-[18rem] flex-col justify-center rounded-2xl border border-dashed border-slate-800 bg-slate-950/40 p-6 text-slate-400">
                <p className="max-w-lg text-sm leading-6">
                  {adminView === 'failures'
                    ? 'Select a failure to inspect what happened, or clear it from the list if it is not useful.'
                    : 'Select a queue card to open its verification notes here, or create a verified product manually.'}
                </p>
                <button type="button" onClick={startManualProduct} className="mt-4 inline-flex w-fit items-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 font-semibold text-white transition hover:bg-slate-800">
                  <Plus className="h-4 w-4" />
                  New Verified Product
                </button>
              </div>
            ) : adminView === 'failures' && selected ? (
              <div className="space-y-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold uppercase tracking-[0.18em] text-amber-300">Incident Details</p>
                    <h2 className="mt-2 text-2xl font-bold text-white">{failureSummary(selected)?.productMatch || failureSummary(selected)?.query || 'Backend error'}</h2>
                    <p className="mt-2 text-sm leading-6 text-slate-400">
                      Use this view to understand what failed, what VegAI tried, and whether this needs a verified product, source fix, or code follow-up.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={closeEditor}
                    className="shrink-0 rounded-xl p-2 text-slate-500 transition hover:bg-slate-800 hover:text-slate-100"
                    aria-label="Close incident"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="grid gap-3 md:grid-cols-4">
                  <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Reason</p>
                    <p className="mt-1 break-words text-sm font-semibold text-white">{failureSummary(selected)?.reason || 'unknown'}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Backend Status</p>
                    <p className="mt-1 break-words text-sm font-semibold text-white">{failureSummary(selected)?.status || 'unknown'}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Resolver</p>
                    <p className="mt-1 break-words text-sm font-semibold text-white">{failureSummary(selected)?.resolvedVia || 'unknown'}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Submitted</p>
                    <p className="mt-1 break-words text-sm font-semibold text-white">{formatDateTime(failureSummary(selected)?.createdAt) || 'unknown'}</p>
                    {failureSummary(selected)?.updatedAt ? (
                      <p className="mt-1 text-xs text-slate-500">Updated {formatDateTime(failureSummary(selected)?.updatedAt)}</p>
                    ) : null}
                  </div>
                </div>

                <div className="rounded-2xl border border-emerald-400/20 bg-emerald-400/10 p-4">
                  <p className="text-sm font-bold text-emerald-100">Plain-English Summary</p>
                  <p className="mt-2 text-sm leading-6 text-emerald-50/90">{plainEnglishFailure(failureSummary(selected))}</p>
                </div>

                <label className="block space-y-2 rounded-2xl border border-slate-800 bg-slate-950/70 p-4 text-sm font-semibold text-white">
                  Investigation Notes
                  <textarea
                    className="h-28 w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm font-normal text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-emerald-400/60"
                    value={reviewNotes}
                    onChange={(event) => setReviewNotes(event.target.value)}
                    placeholder="What happened, what you checked, and what should happen next..."
                  />
                  <span className="block text-xs font-normal text-slate-500">
                    These notes stay with this Command record so future reviews do not depend on chat memory.
                  </span>
                </label>

                <div className="grid gap-4 lg:grid-cols-2">
                  <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
                    <p className="mb-3 text-sm font-bold text-white">User Input</p>
                    <dl className="space-y-2 text-sm text-slate-300">
                      <div className="grid gap-1 sm:grid-cols-[120px_1fr]">
                        <dt className="font-semibold text-slate-500">Query</dt>
                        <dd className="break-words">{failureSummary(selected)?.query || 'Not saved'}</dd>
                      </div>
                      <div className="grid gap-1 sm:grid-cols-[120px_1fr]">
                        <dt className="font-semibold text-slate-500">Match</dt>
                        <dd className="break-words">{failureSummary(selected)?.productMatch || 'No product match saved'}</dd>
                      </div>
                      <div className="grid gap-1 sm:grid-cols-[120px_1fr]">
                        <dt className="font-semibold text-slate-500">User Saw</dt>
                        <dd className="break-words">{failureSummary(selected)?.message || 'No user-facing message saved.'}</dd>
                      </div>
                    </dl>
                  </div>

                  <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
                    <p className="mb-3 text-sm font-bold text-white">Source Evidence</p>
                    <dl className="space-y-2 text-sm text-slate-300">
                      <div className="grid gap-1 sm:grid-cols-[140px_1fr]">
                        <dt className="font-semibold text-slate-500">Source types</dt>
                        <dd>{failureSummary(selected)?.sourceTypes?.join(', ') || 'None saved'}</dd>
                      </div>
                      <div className="grid gap-1 sm:grid-cols-[140px_1fr]">
                        <dt className="font-semibold text-slate-500">Trusted count</dt>
                        <dd>{failureSummary(selected)?.trustedSourceCount ?? 'Unknown'}</dd>
                      </div>
                      <div className="grid gap-1 sm:grid-cols-[140px_1fr]">
                        <dt className="font-semibold text-slate-500">Best source</dt>
                        <dd>{failureSummary(selected)?.bestSourceType || 'Unknown'} {failureSummary(selected)?.bestTrustScore ? `(${failureSummary(selected).bestTrustScore})` : ''}</dd>
                      </div>
                      <div className="grid gap-1 sm:grid-cols-[140px_1fr]">
                        <dt className="font-semibold text-slate-500">Warnings</dt>
                        <dd>{failureSummary(selected)?.warnings?.join(', ') || 'None saved'}</dd>
                      </div>
                    </dl>
                  </div>
                </div>

                {failureSummary(selected)?.sources?.length > 0 && (
                  <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
                    <p className="mb-3 text-sm font-bold text-white">Sources VegAI Returned</p>
                    <ul className="space-y-2 text-sm text-slate-300">
                      {failureSummary(selected).sources.map((source, index) => (
                        <li key={`${source.url || source.title}-${index}`} className="break-words">
                          <a className="text-emerald-300 underline decoration-emerald-300/40 underline-offset-4" href={source.url} target="_blank" rel="noreferrer">
                            {source.title || source.name || source.domain || source.url || 'Source'}
                          </a>
                          {source.label ? <span className="ml-2 text-slate-500">({source.label})</span> : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="grid gap-4 lg:grid-cols-2">
                  <details className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4 text-sm text-slate-300" open>
                    <summary className="cursor-pointer font-bold text-white">Rejected source details</summary>
                    <pre className="mt-3 max-h-72 overflow-auto whitespace-pre-wrap break-words rounded-xl bg-slate-900 p-3 text-xs text-slate-300">
                      {failureSummary(selected)?.rejectedSources?.length ? conciseJson(failureSummary(selected).rejectedSources) : 'No rejected source details saved.'}
                    </pre>
                  </details>
                  <details className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4 text-sm text-slate-300">
                    <summary className="cursor-pointer font-bold text-white">Raw backend result</summary>
                    <pre className="mt-3 max-h-72 overflow-auto whitespace-pre-wrap break-words rounded-xl bg-slate-900 p-3 text-xs text-slate-300">
                      {conciseJson(failureSummary(selected)?.rawResult)}
                    </pre>
                  </details>
                </div>

                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={saveReviewNotes}
                    className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 font-semibold text-slate-300 transition hover:bg-slate-800"
                  >
                    Save Notes
                  </button>
                  <button
                    type="button"
                    onClick={() => changeReviewItemStatus(selected, 'reviewing', 'Marked investigating')}
                    className="rounded-xl border border-sky-400/40 bg-sky-500/15 px-4 py-2 font-semibold text-sky-200 transition hover:bg-sky-500/25"
                  >
                    Mark Investigating
                  </button>
                  <button
                    type="button"
                    onClick={() => changeReviewItemStatus(selected, 'resolved', 'Marked resolved')}
                    className="rounded-xl border border-emerald-400/40 bg-emerald-500/15 px-4 py-2 font-semibold text-emerald-200 transition hover:bg-emerald-500/25"
                  >
                    Mark Resolved
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      window.navigator?.clipboard?.writeText(conciseJson(failureSummary(selected)?.rawResult));
                      setMessage('Copied backend details.');
                    }}
                    className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 font-semibold text-slate-300 transition hover:bg-slate-800"
                  >
                    Copy Debug Details
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setAdminView('queue');
                      setManualMode(true);
                      setForm(prefillFromItem(selected));
                    }}
                    className="rounded-xl border border-emerald-400/40 bg-emerald-500/15 px-4 py-2 font-semibold text-emerald-200 transition hover:bg-emerald-500/25"
                  >
                    Create Verified Product
                  </button>
                  <button type="button" onClick={dismissSelected} className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 font-semibold text-slate-300 transition hover:bg-slate-800">
                    Clear Error
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <h2 className="text-2xl font-bold text-white">{selected ? reviewItemTitle(selected) : 'New Verified Product'}</h2>
                    {selected && (
                      <>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <span className="rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-xs font-semibold text-emerald-200">
                            Result: {selected.lastResult?.status || 'unknown'}
                          </span>
                          <span className="rounded-full border border-slate-700 bg-slate-900 px-3 py-1 text-xs font-semibold text-slate-300">
                            Reason: {selected.reason}
                          </span>
                          {selected.reason === 'user_flag' ? (
                            <span className="rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1 text-xs font-semibold text-amber-200">
                              User flag received
                            </span>
                          ) : null}
                          {userFlagSummary(selected) ? (
                            <span className="rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1 text-xs font-semibold text-amber-100">
                              {userFlagSummary(selected)}
                            </span>
                          ) : null}
                          <span className="rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1 text-xs font-semibold text-amber-200">
                            Flags: {selected.flagCount}
                          </span>
                        </div>
                        <p className="mt-2 text-sm text-slate-500">
                          Submitted {formatDateTime(selected.createdAt) || 'unknown'}
                          {selected.updatedAt ? ` · Updated ${formatDateTime(selected.updatedAt)}` : ''}
                        </p>
                      </>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={closeEditor}
                    className="shrink-0 rounded-xl p-2 text-slate-500 transition hover:bg-slate-800 hover:text-slate-100"
                    aria-label="Close editor"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                {selected?.reason === 'user_flag' ? (
                  <div className="rounded-2xl border border-sky-400/25 bg-sky-400/10 p-4">
                    <label className="block space-y-2 text-sm font-semibold text-sky-100">
                      Admin flag category
                      <div className="flex flex-col gap-2 sm:flex-row">
                        <select
                          value={reviewFlagCategory}
                          onChange={(event) => setReviewFlagCategory(event.target.value)}
                          className="min-w-0 flex-1 rounded-xl border border-sky-300/25 bg-slate-950 px-3 py-2.5 text-sm text-white outline-none focus:border-sky-300/60"
                        >
                          <option value="">Choose a category</option>
                          {FLAG_CATEGORIES.map((category) => (
                            <option key={category.id} value={category.id}>{category.label}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={saveReviewFlagCategory}
                          disabled={!reviewFlagCategory}
                          className="rounded-xl bg-sky-300 px-4 py-2.5 font-semibold text-slate-950 transition hover:bg-sky-200 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Save category
                        </button>
                      </div>
                      <span className="block text-xs font-normal leading-5 text-sky-100/70">
                        VegAI starts with the reason the user chose. Change it here when a different category is more useful for future review.
                      </span>
                    </label>
                  </div>
                ) : null}

                <div className="grid gap-4 md:grid-cols-2">
                  <label className="space-y-1 text-sm font-semibold">
                    Product Name
                    <input className="w-full rounded-xl border px-3 py-2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  </label>
                  <label className="space-y-1 text-sm font-semibold">
                    Brand
                    <input className="w-full rounded-xl border px-3 py-2" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} />
                  </label>
                </div>

                {selected && failureSummary(selected) && (
                  <div className="rounded-2xl border border-amber-400/25 bg-amber-400/10 p-4">
                    <p className="text-sm font-bold text-amber-100">Lookup failure report</p>
                    <div className="mt-3 grid gap-3 text-sm text-amber-50/90 md:grid-cols-3">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-amber-200/80">Error</p>
                        <p className="break-words">{failureSummary(selected).error}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-amber-200/80">Resolver</p>
                        <p>{failureSummary(selected).resolvedVia}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-amber-200/80">Warnings</p>
                        <p>{failureSummary(selected).warnings.join(', ') || 'None saved'}</p>
                      </div>
                    </div>
                    <p className="mt-3 text-sm leading-6 text-amber-50/90">{failureSummary(selected).message}</p>
                    {failureSummary(selected).rejectedSources.length > 0 && (
                      <details className="mt-3 rounded-xl border border-amber-300/20 bg-slate-950/40 p-3 text-xs text-amber-50/90">
                        <summary className="cursor-pointer font-semibold text-amber-100">Rejected source details</summary>
                        <pre className="mt-3 max-h-56 overflow-auto whitespace-pre-wrap break-words">{conciseJson(failureSummary(selected).rejectedSources)}</pre>
                      </details>
                    )}
                  </div>
                )}

                <label className="block space-y-1 text-sm font-semibold">
                  Aliases
                  <textarea className="h-20 w-full rounded-xl border px-3 py-2" value={form.aliases} onChange={(e) => setForm({ ...form, aliases: e.target.value })} />
                </label>

                <div className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
                  <label className="block space-y-1 text-sm font-semibold">
                    Ingredient Evidence / Notes
                    <textarea className="h-40 w-full rounded-xl border px-3 py-2 font-mono text-sm" value={form.ingredients} onChange={(e) => setForm({ ...form, ingredients: e.target.value })} />
                  </label>
                  <label className="block space-y-1 text-sm font-semibold">
                    Reviewer Notes
                    <textarea
                      className="h-40 w-full rounded-xl border px-3 py-2"
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      placeholder="Add concise review notes..."
                    />
                  </label>
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                  <label className="space-y-1 text-sm font-semibold">
                    Verified Status
                    <select className="w-full rounded-xl border px-3 py-2" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                      <option value="vegan">Vegan</option>
                      <option value="not-vegan">Not Vegan</option>
                      <option value="maybe-vegan">Maybe Vegan</option>
                    </select>
                  </label>
                  <label className="space-y-1 text-sm font-semibold">
                    Source Type
                    <input className="w-full rounded-xl border px-3 py-2" value={form.sourceType} onChange={(e) => setForm({ ...form, sourceType: e.target.value })} />
                  </label>
                  <label className="space-y-1 text-sm font-semibold">
                    Recheck by (optional)
                    <input className="w-full rounded-xl border px-3 py-2" placeholder="YYYY-MM-DD" value={form.expiresAt} onChange={(e) => setForm({ ...form, expiresAt: e.target.value })} />
                    <span className="block text-xs font-normal text-slate-500">
                      Leave blank for the default window (90 days, same horizon as when name lookups fall out of cache).
                      After that date the app re-runs live lookup instead of the fast verified path. Enter none to skip
                      expiry (not recommended).
                    </span>
                  </label>
                </div>

                <label className="block space-y-1 text-sm font-semibold">
                  Source URL
                  <input className="w-full rounded-xl border px-3 py-2" value={form.sourceUrl} onChange={(e) => setForm({ ...form, sourceUrl: e.target.value })} />
                </label>

                <label className="block space-y-1 text-sm font-semibold">
                  Verification reason
                  <span className="font-normal text-red-600"> *</span>
                  <textarea
                    className="mt-1 h-24 w-full rounded-xl border px-3 py-2"
                    value={form.verificationReason}
                    onChange={(e) => setForm({ ...form, verificationReason: e.target.value })}
                    placeholder="Why did you choose this verified status? Use N/A only if no reason applies."
                  />
                  <span className="block text-xs font-normal text-slate-500">
                    Required. Enter a short explanation, or <strong className="font-semibold text-slate-600">N/A</strong> (any spacing/case) if there is no specific reason.
                    Whitespace-only or symbol-only text is not accepted.
                  </span>
                </label>

                <div className="grid gap-4 md:grid-cols-1">
                  <label className="space-y-1 text-sm font-semibold">
                    Verified By
                    <input className="w-full rounded-xl border px-3 py-2" value={form.verifiedBy} onChange={(e) => setForm({ ...form, verifiedBy: e.target.value })} />
                  </label>
                </div>

                {selectedSources.length > 0 && (
                  <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
                    <p className="mb-2 text-sm font-bold text-white">Sources from lookup</p>
                    <ul className="space-y-1 text-sm text-slate-300">
                      {selectedSources.map((source, index) => (
                        <li key={`${source.url || source.title}-${index}`}>
                          <a className="text-emerald-300 underline decoration-emerald-300/40 underline-offset-4" href={source.url} target="_blank" rel="noreferrer">
                            {source.title || source.domain || source.url}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex flex-wrap gap-3">
                  {(manualMode || selected) && (
                    <button
                      type="button"
                      onClick={closeEditor}
                    className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 font-semibold text-slate-300 transition hover:bg-slate-800"
                    >
                      Close
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={saveVerified}
                    disabled={!isValidVerificationReason(form.verificationReason)}
                    className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-4 py-2 font-semibold text-slate-950 transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <CheckCircle className="h-4 w-4" />
                    Save Verified Product
                  </button>
                  {selected && (
                    <button type="button" onClick={dismissSelected} className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 font-semibold text-slate-300 transition hover:bg-slate-800">
                      Dismiss
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        ) : adminView === 'feedback' ? (
          <div className="grid gap-4 lg:grid-cols-[380px_1fr]">
            <div className="rounded-3xl border border-slate-800/80 bg-slate-950/60 p-4 shadow-2xl shadow-black/10 backdrop-blur">
              <div className="mb-3 flex items-center justify-between gap-3">
                <h2 className="text-lg font-bold text-white">Feedback Inbox</h2>
                <span className="rounded-full bg-slate-800 px-2.5 py-1 text-xs font-semibold text-slate-300">
                  {feedbackItems.length}
                </span>
              </div>
              <div className="mb-4 flex flex-wrap gap-2">
                {FEEDBACK_FILTERS.map((filter) => (
                  <button
                    key={filter.id}
                    type="button"
                    onClick={async () => {
                      setFeedbackFilter(filter.id);
                      await loadFeedbackInbox(filter.id);
                    }}
                    className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                      feedbackFilter === filter.id
                        ? 'border-emerald-400/40 bg-emerald-400/15 text-emerald-200'
                        : 'border-slate-800 bg-slate-900/70 text-slate-400 hover:border-slate-700 hover:text-slate-200'
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
              {searchedFeedbackItems.length === 0 ? (
                <p className="text-sm text-slate-500">No feedback items for this filter.</p>
              ) : (
                <div className="space-y-3">
                  {searchedFeedbackItems.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => selectFeedback(item)}
                      className={`w-full rounded-2xl border p-4 text-left transition ${
                        selectedFeedback?.id === item.id
                          ? 'border-emerald-400 bg-emerald-400/10 shadow-[0_0_0_1px_rgba(52,211,153,0.18)]'
                          : 'border-slate-800 bg-slate-950/40 hover:border-slate-700 hover:bg-slate-900/70'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-semibold capitalize text-white">{item.category}</p>
                          <p className="mt-1 text-sm text-slate-400">{item.message}</p>
                          <p className="mt-2 text-xs text-slate-500">
                            Submitted {formatDateTime(item.createdAt) || 'unknown'} · {item.status}
                            {item.updatedAt ? ` · Updated ${formatDateTime(item.updatedAt)}` : ''}
                          </p>
                        </div>
                        <span className="mt-1 text-slate-500">›</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div id="command-feedback-detail" className="admin-dark-surface scroll-mt-6 rounded-3xl border border-slate-800/80 bg-slate-950/60 p-4 shadow-2xl shadow-black/10 backdrop-blur sm:p-6">
              {!selectedFeedback ? (
                <div className="flex min-h-[18rem] flex-col justify-center rounded-2xl border border-dashed border-slate-800 bg-slate-950/40 p-6 text-slate-400">
                  <p className="max-w-lg text-sm leading-6">
                    Select a feedback item to review the user message, context, correction details, and next action.
                  </p>
                </div>
              ) : (
                <div className="space-y-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-bold uppercase tracking-[0.18em] text-emerald-300">
                        Feedback #{selectedFeedback.id}
                      </p>
                      <h2 className="mt-2 text-2xl font-bold capitalize text-white">{selectedFeedback.category}</h2>
                      <p className="mt-1 text-sm text-slate-500">
                        Created {formatDateTime(selectedFeedback.createdAt) || 'unknown'} · Status {selectedFeedback.status}
                        {selectedFeedback.updatedAt ? ` · Updated ${formatDateTime(selectedFeedback.updatedAt)}` : ''}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={closeEditor}
                      className="shrink-0 rounded-xl p-2 text-slate-500 transition hover:bg-slate-800 hover:text-slate-100"
                      aria-label="Close feedback"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
                    <p className="mb-2 text-sm font-bold text-white">User message</p>
                    <p className="whitespace-pre-wrap text-sm leading-6 text-slate-300">{selectedFeedback.message}</p>
                  </div>

                  {selectedFeedback.contactEmail ? (
                    <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
                      <p className="mb-2 text-sm font-bold text-white">Contact</p>
                      <p className="break-words text-sm text-slate-300">{selectedFeedback.contactEmail}</p>
                    </div>
                  ) : null}

                  {selectedFeedback.context ? (
                    <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
                      <p className="mb-2 text-sm font-bold text-white">Context</p>
                      <dl className="space-y-1 text-sm text-slate-300">
                        {Object.entries(selectedFeedback.context).map(([key, value]) => (
                          <div key={key} className="grid gap-1 sm:grid-cols-[130px_1fr]">
                            <dt className="font-semibold text-slate-500">{key}</dt>
                            <dd className="break-words">{String(value)}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  ) : null}

                  {selectedFeedback.correction || selectedFeedback.correctionResult ? (
                    <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
                      <p className="mb-2 text-sm font-bold text-white">Correction</p>
                      {selectedFeedback.correction ? (
                        <pre className="overflow-auto rounded-xl bg-slate-900 p-3 text-xs text-slate-300">
                          {JSON.stringify(selectedFeedback.correction, null, 2)}
                        </pre>
                      ) : null}
                      {selectedFeedback.correctionResult ? (
                        <p className="mt-2 text-sm text-slate-300">
                          Result: {selectedFeedback.correctionResult.applied ? 'applied' : selectedFeedback.correctionResult.reason}
                        </p>
                      ) : null}
                      {selectedFeedback.correction && selectedFeedback.correctionResult?.reason === 'pending-admin-review' ? (
                        <p className="mt-2 text-sm text-amber-200">
                          This correction is waiting for admin approval before it changes VegAI rules.
                        </p>
                      ) : null}
                    </div>
                  ) : null}

                  <label className="block space-y-1 text-sm font-semibold">
                    Admin Notes
                    <textarea
                      className="h-28 w-full rounded-xl border px-3 py-2"
                      value={feedbackNotes}
                      onChange={(e) => setFeedbackNotes(e.target.value)}
                      placeholder="What did you do with this feedback?"
                    />
                  </label>

                  <div className="flex flex-wrap gap-3">
                    {selectedFeedback.correction ? (
                      <button type="button" onClick={applySelectedFeedbackCorrection} className="rounded-xl bg-amber-400 px-4 py-2 font-semibold text-slate-950 transition hover:bg-amber-300">
                        Apply Correction
                      </button>
                    ) : null}
                    <button type="button" onClick={() => changeFeedbackStatus('reviewing')} className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 font-semibold text-slate-300 transition hover:bg-slate-800">
                      Mark Reviewing
                    </button>
                    <button type="button" onClick={() => changeFeedbackStatus('resolved')} className="rounded-xl bg-emerald-500 px-4 py-2 font-semibold text-slate-950 transition hover:bg-emerald-400">
                      Resolve
                    </button>
                    <button type="button" onClick={() => changeFeedbackStatus('dismissed')} className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 font-semibold text-slate-300 transition hover:bg-slate-800">
                      Dismiss
                    </button>
                    <button type="button" onClick={() => changeFeedbackStatus('open')} className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 font-semibold text-slate-300 transition hover:bg-slate-800">
                      Reopen
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="rounded-3xl border border-slate-800/80 bg-slate-950/60 p-6 shadow-2xl shadow-black/10 backdrop-blur">
              <h2 className="mb-2 text-lg font-bold text-white">Verified products</h2>
              <p className="mb-4 text-sm text-slate-400">
                Click a saved verification to open its notes and editable details below the catalog.
              </p>
              <div className="mb-4 grid gap-3 sm:grid-cols-4">
                <div className="rounded-2xl border border-slate-800 bg-slate-950/50 p-3">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Total</p>
                  <p className="mt-1 text-2xl font-bold text-white">{verifiedCatalogCounts.total}</p>
                </div>
                <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-3">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-200/70">Needs review</p>
                  <p className="mt-1 text-2xl font-bold text-amber-100">{verifiedCatalogCounts.needsReview}</p>
                </div>
                <div className="rounded-2xl border border-yellow-500/30 bg-yellow-500/10 p-3">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-yellow-200/70">Expiring soon</p>
                  <p className="mt-1 text-2xl font-bold text-yellow-100">{verifiedCatalogCounts.expiringSoon}</p>
                </div>
                <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-3">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-200/70">Current</p>
                  <p className="mt-1 text-2xl font-bold text-emerald-100">{verifiedCatalogCounts.current}</p>
                </div>
              </div>
              <div className="mb-4 flex flex-wrap gap-2">
                {VERIFIED_REVIEW_FILTERS.map((filter) => (
                  <button
                    key={filter.id}
                    type="button"
                    onClick={() => setVerifiedReviewFilter(filter.id)}
                    className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                      verifiedReviewFilter === filter.id
                        ? 'border-emerald-400 bg-emerald-400/15 text-emerald-100'
                        : 'border-slate-800 bg-slate-950/40 text-slate-400 hover:border-slate-700 hover:text-slate-200'
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
              {searchedVerifiedRows.length === 0 ? (
                <p className="text-sm text-slate-500">No verified records loaded. Use Refresh catalog.</p>
              ) : (
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {searchedVerifiedRows.map((row) => {
                    const badge = verifiedReviewBadge(row);
                    return (
                      <button
                        key={row.id}
                        type="button"
                        onClick={() => selectVerifiedRow(row)}
                        className={`w-full rounded-2xl border p-4 text-left transition ${
                          editingVerified?.id === row.id
                            ? 'border-emerald-400 bg-emerald-400/10 shadow-[0_0_0_1px_rgba(52,211,153,0.18)]'
                            : 'border-slate-800 bg-slate-950/40 hover:border-slate-700 hover:bg-slate-900/70'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-semibold text-white">{row.name}</p>
                            {row.brand ? <p className="text-sm text-slate-400">{row.brand}</p> : null}
                          </div>
                          <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${badge.className}`}>
                            {badge.label}
                          </span>
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-400">
                          <span>
                            Result: <strong className="text-emerald-300">{row.status}</strong>
                          </span>
                          {Number.isFinite(Number(row.daysUntilExpiry)) ? (
                            <span>{Number(row.daysUntilExpiry) < 0 ? `${Math.abs(Number(row.daysUntilExpiry))} days overdue` : `${Number(row.daysUntilExpiry)} days left`}</span>
                          ) : (
                            <span>No recheck date</span>
                          )}
                        </div>
                        <p className="mt-1 text-xs text-slate-500">
                          Updated {formatDate(row.updatedAt || row.verifiedAt)}
                          {row.expiresAt ? ` · Recheck by ${formatDate(row.expiresAt)}` : ''}
                        </p>
                        {Array.isArray(row.aliases) && row.aliases.length > 0 ? (
                          <p className="mt-2 break-words text-xs text-slate-500">
                            <span className="font-semibold text-slate-300">Aliases: </span>
                            {row.aliases.join(' · ')}
                          </p>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {editingVerified && (
              <div id="command-verified-editor" className="admin-dark-surface scroll-mt-6 rounded-3xl border border-slate-800/80 bg-slate-950/60 p-6 shadow-2xl shadow-black/10 backdrop-blur">
                <div className="space-y-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h2 className="text-2xl font-bold text-white">{editingVerified.name}</h2>
                      <p className="text-sm text-slate-500">Verified catalog record #{editingVerified.id}</p>
                    </div>
                    <button
                      type="button"
                      onClick={closeEditor}
                      className="shrink-0 rounded-xl p-2 text-slate-500 transition hover:bg-slate-800 hover:text-slate-100"
                      aria-label="Close editor"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <label className="space-y-1 text-sm font-semibold">
                      Product Name
                      <input className="w-full rounded-xl border px-3 py-2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    </label>
                    <label className="space-y-1 text-sm font-semibold">
                      Brand
                      <input className="w-full rounded-xl border px-3 py-2" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} />
                    </label>
                  </div>

                  <label className="block space-y-1 text-sm font-semibold">
                    Aliases
                    <textarea className="h-24 w-full rounded-xl border px-3 py-2" value={form.aliases} onChange={(e) => setForm({ ...form, aliases: e.target.value })} />
                  </label>

                  <label className="block space-y-1 text-sm font-semibold">
                    Ingredients
                    <textarea className="h-40 w-full rounded-xl border px-3 py-2 font-mono text-sm" value={form.ingredients} onChange={(e) => setForm({ ...form, ingredients: e.target.value })} />
                  </label>

                  <div className="grid gap-4 md:grid-cols-3">
                    <label className="space-y-1 text-sm font-semibold">
                      Verified Status
                      <select className="w-full rounded-xl border px-3 py-2" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                        <option value="vegan">Vegan</option>
                        <option value="not-vegan">Not Vegan</option>
                        <option value="maybe-vegan">Maybe Vegan</option>
                      </select>
                    </label>
                    <label className="space-y-1 text-sm font-semibold">
                      Source Type
                      <input className="w-full rounded-xl border px-3 py-2" value={form.sourceType} onChange={(e) => setForm({ ...form, sourceType: e.target.value })} />
                    </label>
                    <label className="space-y-1 text-sm font-semibold">
                      Recheck by
                      <input className="w-full rounded-xl border px-3 py-2" placeholder="YYYY-MM-DD" value={form.expiresAt} onChange={(e) => setForm({ ...form, expiresAt: e.target.value })} />
                    </label>
                  </div>

                  <label className="block space-y-1 text-sm font-semibold">
                    Source URL
                    <input className="w-full rounded-xl border px-3 py-2" value={form.sourceUrl} onChange={(e) => setForm({ ...form, sourceUrl: e.target.value })} />
                  </label>

                  <label className="block space-y-1 text-sm font-semibold">
                    Verification reason
                    <span className="font-normal text-red-600"> *</span>
                    <textarea
                      className="mt-1 h-20 w-full rounded-xl border px-3 py-2"
                      value={form.verificationReason}
                      onChange={(e) => setForm({ ...form, verificationReason: e.target.value })}
                    />
                  </label>

                  <div className="grid gap-4 md:grid-cols-2">
                    <label className="space-y-1 text-sm font-semibold">
                      Verified By
                      <input className="w-full rounded-xl border px-3 py-2" value={form.verifiedBy} onChange={(e) => setForm({ ...form, verifiedBy: e.target.value })} />
                    </label>
                    <label className="space-y-1 text-sm font-semibold">
                      Notes
                      <textarea className="h-20 w-full rounded-xl border px-3 py-2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                    </label>
                  </div>

                  {selectedSources.length > 0 && (
                    <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
                      <p className="mb-2 text-sm font-bold text-white">Saved sources</p>
                      <ul className="space-y-1 text-sm text-slate-300">
                        {selectedSources.map((source, index) => (
                          <li key={`${source.url || source.title}-${index}`}>
                            {source.url ? (
                              <a className="text-emerald-300 underline decoration-emerald-300/40 underline-offset-4" href={source.url} target="_blank" rel="noreferrer">
                                {source.title || source.domain || source.url}
                              </a>
                            ) : (
                              <span>{source.title || source.domain || source.type || 'Source'}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={closeEditor}
                      className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 font-semibold text-slate-300 transition hover:bg-slate-800"
                    >
                      Close
                    </button>
                    <button
                      type="button"
                      onClick={saveVerified}
                      disabled={!isValidVerificationReason(form.verificationReason)}
                      className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-4 py-2 font-semibold text-slate-950 transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <CheckCircle className="h-4 w-4" />
                      Save Changes
                    </button>
                    <button
                      type="button"
                      onClick={deleteVerified}
                      disabled={loading}
                      className="inline-flex items-center gap-2 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-2 font-semibold text-red-200 transition hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Trash2 className="h-4 w-4" />
                      {confirmingVerifiedDeleteId === editingVerified.id ? 'Confirm Delete' : 'Delete Verified Record'}
                    </button>
                    {confirmingVerifiedDeleteId === editingVerified.id ? (
                      <button
                        type="button"
                        onClick={() => {
                          setConfirmingVerifiedDeleteId(null);
                          setMessage('');
                        }}
                        className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 font-semibold text-slate-300 transition hover:bg-slate-800"
                      >
                        Cancel Delete
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {!settingsOpen ? (
          <CommandFeedback
            error={error}
            message={message}
            lastDeletedVerified={lastDeletedVerified}
            onUndoDelete={undoVerifiedDelete}
            loading={loading}
          />
        ) : null}
      </div>
      {settingsOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 backdrop-blur-sm sm:p-8">
          <div className="w-full max-w-5xl rounded-3xl border border-slate-800 bg-slate-950 p-5 shadow-2xl shadow-black/50">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-bold uppercase tracking-[0.18em] text-emerald-300">Command Settings</p>
                <h2 className="mt-2 text-2xl font-bold text-white">Health and session controls</h2>
                <p className="mt-1 text-sm leading-6 text-slate-400">
                  Settings live here so the main workspace stays focused on daily operations.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSettingsOpen(false)}
                className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-800 hover:text-slate-100"
                aria-label="Close settings"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <WorkCard title="Service Health" actionLabel="Run checks" onAction={runServiceChecks}>
                <HealthRow
                  label="Backend"
                  value={serviceHealth?.status === 'ok' ? 'ok' : serviceHealth?.status || 'unknown'}
                  detail={serviceHealth?.service || serviceHealth?.message || 'Checks /health/deep through the current app connection.'}
                />
                <HealthRow
                  label="Database"
                  value={serviceHealth?.checks?.db?.ok ? 'ok' : serviceHealth ? 'review' : 'unknown'}
                  detail={serviceHealth?.checks?.db?.productCache ? `Cache total: ${serviceHealth.checks.db.productCache.total}` : 'Product cache and local SQLite status.'}
                />
                <HealthRow
                  label="OpenAI key"
                  value={serviceHealth?.classifier?.openAIKey?.present ? 'ok' : serviceHealth ? 'review' : 'unknown'}
                  detail={serviceHealth?.classifier?.model ? `Model: ${serviceHealth.classifier.model}` : 'Presence only unless an intentional OpenAI check is run.'}
                />
                <HealthRow
                  label="Feature flags"
                  value={
                    serviceHealth?.featureFlags?.disabled?.length > 0
                      ? 'review'
                      : serviceHealth?.featureFlags
                        ? 'ok'
                        : 'unknown'
                  }
                  detail={
                    serviceHealth?.featureFlags
                      ? `${serviceHealth.featureFlags.disabled?.length || 0} disabled; ${serviceHealth.featureFlags.flags?.length || 0} tracked.`
                      : 'Controlled rollout switches for public analysis, lookup, image, and feedback features.'
                  }
                />
                <HealthRow
                  label="Cost controls"
                  value={
                    serviceHealth?.costControls?.emergencyPaused
                      ? 'review'
                      : serviceHealth?.costControls?.enabled
                        ? 'ok'
                        : serviceHealth
                          ? 'review'
                          : 'unknown'
                  }
                  detail={
                    serviceHealth?.costControls
                      ? `Analysis ${serviceHealth.costControls.features?.analysis?.remainingThisHour ?? 'unknown'} left this hour; image ${serviceHealth.costControls.features?.image?.remainingThisHour ?? 'unknown'} left.`
                      : 'App-side usage guardrails for expensive AI and image requests.'
                  }
                />
                <HealthRow
                  label="USDA"
                  value={serviceHealth?.integrations?.usda?.configured ? 'ok' : serviceHealth ? 'review' : 'unknown'}
                  detail={serviceHealth?.integrations?.usda?.enabled === false ? 'Disabled in backend config.' : 'Checks whether the USDA key is configured for product lookup fallback.'}
                />
                <HealthRow
                  label="Open Food Facts"
                  value={serviceHealth?.integrations?.openFoodFacts?.enabled === false ? 'review' : serviceHealth ? 'ok' : 'unknown'}
                  detail="Primary public product-source integration for barcode and product lookups."
                />
                <HealthRow
                  label="Tailscale"
                  value={window.location.hostname.endsWith('.ts.net') ? 'ok' : 'review'}
                  detail={window.location.hostname.endsWith('.ts.net') ? window.location.hostname : 'Not currently opened through the Tailscale URL.'}
                />
                <HealthRow
                  label="Frontend"
                  value="ok"
                  detail={`Command loaded from ${window.location.host || 'local browser'}.`}
                />
                <div className="rounded-2xl border border-slate-800 bg-slate-950/50 p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="font-semibold text-white">Sentry event test</p>
                      <p className="mt-1 text-sm leading-6 text-slate-400">
                        Sends one frontend event and one backend event to confirm both projects are receiving data.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={sendSentryTestEvents}
                      disabled={loading}
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-2 font-semibold text-emerald-200 transition hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                      Send test
                    </button>
                  </div>
                </div>
              </WorkCard>

              <WorkCard title="Cost Activity" actionLabel="Refresh" onAction={runServiceChecks}>
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="rounded-2xl border border-slate-800 bg-slate-950/50 p-4">
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Allowed</p>
                    <p className="mt-2 text-2xl font-bold text-white">{costActivity?.summary?.allowed24h ?? 0}</p>
                    <p className="mt-1 text-xs text-slate-500">Last 24 hours</p>
                  </div>
                  <div className="rounded-2xl border border-slate-800 bg-slate-950/50 p-4">
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Blocked</p>
                    <p className="mt-2 text-2xl font-bold text-white">{costActivity?.summary?.blocked24h ?? 0}</p>
                    <p className="mt-1 text-xs text-slate-500">Budget guardrails</p>
                  </div>
                  <div className="rounded-2xl border border-slate-800 bg-slate-950/50 p-4">
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Window</p>
                    <p className="mt-2 text-2xl font-bold text-white">{costActivity?.summary?.window || '24 hours'}</p>
                    <p className="mt-1 text-xs text-slate-500">Privacy-safe metadata only</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {(costActivity?.events || []).slice(0, 6).map((event) => (
                    <div key={event.id} className="flex items-center justify-between gap-3 rounded-2xl border border-slate-800 bg-slate-950/50 p-3">
                      <div>
                        <p className="font-semibold text-white">
                          {event.feature === 'image' ? 'Image request' : 'Analysis request'}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">
                          {[formatEventTime(event.createdAt), event.source || event.mode, event.reason].filter(Boolean).join(' · ')}
                        </p>
                      </div>
                      <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${event.allowed ? 'bg-emerald-400/15 text-emerald-200' : 'bg-amber-400/15 text-amber-200'}`}>
                        {event.allowed ? 'allowed' : 'blocked'}
                      </span>
                    </div>
                  ))}
                  {(!costActivity?.events || costActivity.events.length === 0) && (
                    <p className="rounded-2xl border border-dashed border-slate-800 p-4 text-sm text-slate-500">
                      No cost activity logged yet.
                    </p>
                  )}
                </div>
              </WorkCard>

              <WorkCard title="Session and Naming">
                <div className="rounded-2xl border border-slate-800 bg-slate-950/50 p-4">
                  <p className="font-semibold text-white">Admin session</p>
                  <p className="mt-1 text-sm leading-6 text-slate-400">
                    The admin secret is kept only in app memory. Refreshing the browser, closing the tab, or logging out requires the secret again.
                  </p>
                  <button
                    type="button"
                    onClick={logout}
                    className="mt-4 inline-flex items-center gap-2 rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-2 font-semibold text-red-200 transition hover:bg-red-500/20"
                  >
                    <LogOut className="h-4 w-4" />
                    Log out
                  </button>
                </div>
                <div className="rounded-2xl border border-slate-800 bg-slate-950/50 p-4">
                  <p className="font-semibold text-white">Naming</p>
                  <p className="mt-1 text-sm leading-6 text-slate-400">
                    Use Command for this admin operations workspace. Use Vision Board for the personal planning dashboard.
                  </p>
                </div>
              </WorkCard>
              <CommandFeedback
                error={error}
                message={message}
                lastDeletedVerified={lastDeletedVerified}
                onUndoDelete={undoVerifiedDelete}
                loading={loading}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
